import { unique, ireverse } from '@vaaas/fpts/array';
import { pipe } from '@vaaas/fpts/function';
import { filter, map, bind, flatten, each } from '@vaaas/fpts/iter';
import { animate } from './scene';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import * as THREE from 'three';
import HashTable from './hashtable';
import * as maths from './maths';
import * as vector3 from './vector3';
import { uil_add_joint_smoothness, uil_remove_joint_smoothness } from './uil_continuity';
import {
  add_edge,
  add_vertex,
  add_vertex_at_param,
  calc_ray_dir_for_face_sampling,
  can_deflate,
  can_inflate,
  can_make_edges_circular,
  can_make_face_circular,
  can_redo,
  can_undo,
  change_object_fixedness,
  change_object_visibility,
  copy_body,
  deflate_face,
  deform_edge,
  delete_body,
  delete_faces,
  delete_vertex,
  end_compound_operation,
  end_deform_edge,
  end_move_edge_list,
  end_move_vertices,
  end_offset_face,
  end_rotate_vertex_normal,
  end_scale_edge_list,
  find_edge_shared_vertex,
  flip_body_inside_out,
  get_body_of_edge,
  get_body_of_face,
  get_body_of_vertex,
  get_display_face_triangulation_res,
  get_display_face_triangulation_res_for_drag,
  get_edges_of_face,
  get_edges_of_vertex,
  get_edge_curvatures,
  get_edge_fixing_of_tangent_at_vertex,
  get_edge_freedom,
  get_edge_length,
  get_edge_list_geometry,
  get_edge_num_of_face,
  get_edge_num_of_vertex,
  get_edge_planes_from_point_intersection,
  get_edge_tangent_at_vertex,
  get_faces_of_edge,
  get_face_color,
  get_face_connected_components,
  get_face_connected_component_info,
  get_face_crosshair_at_params,
  get_face_geometry,
  get_body_label,
  get_face_label,
  get_face_material,
  get_face_normal_at_edge_point,
  get_face_ray_intersection,
  get_face_superedges,
  get_face_triangulation,
  get_face_triangulation_info,
  get_model_bodies,
  get_num_of_model_bodies,
  get_point_at_edge_param,
  get_ray_edge_intersection,
  get_sedge_num_of_face,
  get_tangent_at_edge_param,
  get_vertex_normal,
  get_vertices_of_edge,
  get_world_scaling_factor,
  inflate_face,
  is_edge_linear,
  is_edge_linear_geometry,
  is_joint_smooth,
  is_object_fixed,
  is_object_visible,
  is_vertex_of_edge,
  is_vertex_of_face,
  is_vertex_smooth,
  listify_edges,
  load_model,
  make_edges_circular,
  make_edges_linear_as_one,
  make_edges_planar,
  can_make_edges_symmetric,
  make_edges_symmetric,
  make_face_circular,
  make_hole_or_handle_multi,
  merge_bodies_on_edge,
  modify_edge_geometry,
  move_edge_list,
  move_vertex_on_edge,
  move_vertex_to_coords,
  move_vertices,
  offset_face_drag_step,
  pop_face,
  pop_faces,
  redo,
  rotate_vertex_normal,
  save_model,
  save_undo_state,
  scale_edge_list,
  set_add_edge_pair_tangencies_on_load,
  set_auto_add_tangencies,
  set_display_face_triangulation_res,
  set_display_face_triangulation_res_for_drag,
  set_face_tessel,
  set_edge_fixing_of_tangent_at_vertex,
  set_edge_freedoms,
  set_edge_tangent_at_vertex,
  set_face_color,
  set_body_label,
  set_face_label,
  set_face_material,
  set_linearity_of_edges,
  set_save_model_compression_level,
  set_vertex_distance,
  set_world_scaling_factor,
  start_compound_operation,
  start_deform_edge,
  start_move_edge_list,
  start_move_vertex_on_edge,
  start_move_vertices,
  start_offset_face,
  start_rotate_vertex_normal,
  start_scale_edge_list,
  start_undo_tracking,
  stop_undo_tracking,
  triangulate_faces,
  undo,
  is_joint_geom_smooth,
} from './JCore/logging_jcore';
import { WrappedValue } from '@phi/wrapped-value';
import { add_to_symmetry_set, faces_of_vertex, isVertex, vertices_of_face } from './lattice';
import { display_tangencies, hide_tangencies } from './uid_continuity';
import { uid_deselect_all_objects, uid_deselect_object, uid_select_object, uid_calculate_bounding_box_of_objects, uid_start_scale_objects, uid_show_rigid_move_manipulator, uid_end_scale_objects, uid_hide_rigid_move_manipulator } from './ui_display';
import { Vector3 } from 'three';
import { uid_unhighlight_all_objects } from './colours_materials_appearance';
import { suppress_zero_thickness_checking,
  push_nodups,
  suppress_undo_tracking,
  console_log_xml,
  unsuppress_undo_tracking_if_ready,
  adjust_camera_position_along_axis_and_depth,
  suppress_animate,
  unsuppress_animate_if_ready,
  check_model_integrity,
  update_dom_text_values_and_coordinates,
  request_setting_button_states,
  raycaster_intersect_objects,
  remove_tangencies_from_visible_invisible_edge_pairs,
  core_face_tessellation_is_suppressed,
  reset_core_face_tessellation,
} from './utility';
import App from './application-container';
import { handle_extra_data } from './extra_phi_file_data';
import { edge_is_for_sketch, face_is_for_sketch, number_of_non_deleted_reference_sketches, stop_editing_sketches, vertex_is_for_sketch } from './reference_sketches';
import { triangle_normal, optimal_face_triangles_for_drag } from './geometry_and_math';
import { force_refresh } from './state/force-refresh';
import { hide_indicated_vertices, indicate_moving_vertices, indicate_selected_vertices, make_selected_coordinates_invisible, moving_coordinates_visible } from '../../../../src/features/indicate-vertices';
import {
    curvature_edges,
    show_edge_curvatures,
} from '../../../../src/features/curvature-combs';

function units_factor(units) {
    switch (units) {
        case 'cm': return 1;
        case 'ft': return 30.48;
        case 'in': return 2.54;
        case 'm': return 100;
        case 'mm': return 0.1;
        case 'yd': return 91.44;
        default: return 1;
    }
}

function dimension_factor(model, dx, diameter) {
    switch (model) {
        case 'cube':
        case 'cube_with_hole':
        case 'square':
            return dx;

        case 'cylinder':
        case 'cylinder_with_hole':
        case 'circle':
            return diameter;

        default:
            return 1.0;
    }
}

export async function uil_load_model(str, is_default_model) {
  make_selected_coordinates_invisible();
  suppress_undo_tracking();
  suppress_animate();

  // If it's a default model we're loading, calculate an additional scale factor. This will be used to scale
  // The entire new model (ony body) while loading it. This scale happens at the core side. Then after the
  // loading is complete we'll have to apply additional scaling along some of the orthogonal directions,
  // to bring it to the desired dimensions, as specified in the load model modal.
  // This data is stored in the global_default_model_data object.
  const data = global_default_model_data;
  const additional_scale_factor = is_default_model
    ? units_factor(data.units) * dimension_factor(data.model, data.dx, data.diameter)
    : 1.0;

  // Create a list with all of the current body ids, simply so that we can identify the new body id
  // after we have loaded it.
  const body_ids_before_load = pipe(
    uid_faces.keys(),
    map(uil_get_body_of_face),
    unique,
  );

  const returned_array = load_model(str, window.loading_into_current_model, additional_scale_factor, 0);

  if (!returned_array) {
    display_message_to_user(LOG.WARN, "Failed to load model.");
    return;
  }

  // Perform additional scaling of loaded model, if necessary
  if (is_default_model) {
    uil_perform_additional_scaling_for_default_model(body_ids_before_load);
  }

  const extra_data_string = returned_array[1];
  console_log("Extra data:", extra_data_string);

  if (!window.loading_into_current_model) {
    await handle_extra_data(extra_data_string, is_default_model);
    uid_unset_dirty_flag("load_model");
  }

  // Higlight and unhilight all objects, to give them the correct appearance.
  uid_unhighlight_all_objects();

  // Move all sketch objects into the appropriate node
  for (const m of ireverse(uid_objects_raynode.children)) {
    const uid_obj = uid_object3dIDToUid_object.getItem(m.id);
    if (!uid_obj) continue;
    else if (uid_obj.type == "face" && face_is_for_sketch(uid_obj.id))
        current_scene_raynode_for_sketch_objects().add(m);
    else if (uid_obj.type == "edge" && edge_is_for_sketch(uid_obj.id))
        current_scene_raynode_for_sketch_objects().add(m);
    else if (uid_obj.type == "vertex" && vertex_is_for_sketch(uid_obj.id))
        current_scene_raynode_for_sketch_objects().add(m);
  }

  App.state.currently_loading_model = false;

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  request_setting_button_states();
}

function uil_perform_additional_scaling_for_default_model(body_ids_before_load) {
    // Identify the id of the new, just loaded body, by looking at the list of bodies before the load.
    const new_body_id = uid_faces.keys()
        .map(fid => uil_get_body_of_face(fid))
        .find(bid => !body_ids_before_load.includes(bid))

    if (!new_body_id) {
        console.log("Error trying to identify newly loaded default model body id");
        return;
    }

    // Will now scale body depending on model shape
    // Find all the edges of the new body
    const edges_to_scale = pipe(
        uid_edges.keys(),
        filter(eid => uil_get_body_of_edge(eid) === new_body_id),
        unique,
    );

    for (const eid of edges_to_scale)
        uid_select_object(eid);

    // Scale depending on the model shape
    switch (global_default_model_data.model) {
        case 'square': {
            // Scale in the Y direction
            uid_start_scale_objects(false);
            uid_scale_objects(
                global_default_model_data.dy / global_default_model_data.dx,
                new THREE.Vector3(0.0, 1.0, 0.0),
                false,
                new Vector3(0, 0, 0),
            );
            uid_end_scale_objects();
            break;
        }

        case 'cylinder':
        case 'cylinder_with_hole': {
            // Scale on the Z direction
            uid_start_scale_objects(false);
            uid_scale_objects(
                global_default_model_data.height / global_default_model_data.diameter,
                new THREE.Vector3(0.0, 0.0, 1.0),
                false,
                new Vector3(0, 0, 0),
            );
            uid_end_scale_objects();
            break;
        }

        case 'cube':
        case 'cube_with_hole': {
            // Scale in the Y and Z directions
            uid_start_scale_objects(false);
            uid_scale_objects(
                global_default_model_data.dy / global_default_model_data.dx,
                new THREE.Vector3(0.0, 1.0, 0.0),
                false,
                new Vector3(0, 0, 0),
            );
            uid_end_scale_objects();

            uid_start_scale_objects(false);
            uid_scale_objects(
                global_default_model_data.dz / global_default_model_data.dx,
                new THREE.Vector3(0.0, 0.0, 1.0),
                false,
                new Vector3(0, 0, 0),
            );
            uid_end_scale_objects();
            break;
        }

        case 'circle':
        default:
            // No scaling required
            break;
    }

    for (const eid of edges_to_scale)
        uid_deselect_object(eid);
}

export function uil_get_model_bodies() {
  const nbodies = get_num_of_model_bodies();
  if (nbodies > 0) {
    const arr = get_model_bodies(nbodies);
    if (Array.isArray(arr))
      return arr;
    else {
      console_log_xml("Error in get_model_bodies");
      return [];
    }
  }
  return [];
}

export function uil_is_body_open(bid) {
  //return is_body_open(bid);
  return true; // XXX We hijack the open/closed body mechanism, and treat all bodies as open.
}

export function uil_get_body_of_face(id){
  const bid = get_body_of_face(id);
  if (bid > 0)
    return bid;
  console_log_xml("Error in get_body_of_face");
  return 0;
}

export function uil_get_body_of_edge(id){
  const bid = get_body_of_edge(id);
  if (bid > 0)
    return bid;
  console_log_xml("Error in get_body_of_edge");
  return 0;
}

export function uil_get_body_of_vertex(id){
  const bid = get_body_of_vertex(id);
  if (bid > 0)
    return bid;
  console_log_xml("Error in get_body_of_vertex");
  return 0;
}

export function uil_drag_vertex(vertexid, origin, direction) {
  hide_debug_objs(true);
  //console_log("Drag vertex");

  // Create list of all vertices to drag together
  var vertices_to_drag = [vertexid];
  if (uid_additional_drag_vertices.length > 0)
    vertices_to_drag.push(...uid_additional_drag_vertices);

  // Check for fixing
  var cannot_drag_due_to_fixing = false;
  vertices_to_drag.forEach(function(vid){
    if (uil_is_object_fixed(vid))
      cannot_drag_due_to_fixing = true;
  });
  if (!cannot_drag_due_to_fixing) {
    vertices_to_drag.forEach(function(vid){
      var l = uil_get_edges_of_vertex(vid);
      l.forEach(function(eid){
        if (uil_is_object_fixed(eid))
          cannot_drag_due_to_fixing = true;
      })
    });
  }

  if (cannot_drag_due_to_fixing) {
    display_message_to_user(LOG.WARN, "Cannot move vertex due to fixed objects.");
  }
  else {
    // Check how many objects are selected
    var nsel_v = uid_selected_vertices.length;
    var nsel_e = uid_selected_edges.length;
    var nsel_f = uid_selected_faces.length;

    if (nsel_v == 0 && nsel_e == 0 && nsel_f == 0) {
      // Simple 3D drag of the vertex
      App.state.dragging_type = "vertex";
      if (uid_state.in_first_drag_step) {

        // Check whether we are dragging a vertex between two edges that are flagged and linear and also colinear and also tangent.
        // In this case we want to break their tangency.
        // XXX Must do this for the additional vertex ids too XXX
        var edge_ids = uil_get_edges_of_vertex(vertexid);
        for (var i = edge_ids.length-1; i >= 0; i--) {
          if (!uil_is_edge_linear(edge_ids[i]))
            edge_ids.splice(i, 1);
        }
        suppress_undo_tracking();
        if (edge_ids.length >= 2) {
          edge_ids.forEach(function(e1id){
            edge_ids.forEach(function(e2id) {
              if ((e1id != e2id) && uil_is_joint_smooth(e1id, e2id)) {
                uil_remove_joint_smoothness([[e1id, e2id]], false, true);
              }
            });
          });
        }
        unsuppress_undo_tracking_if_ready(true);

        if (moving_coordinates_visible.value)
          indicate_moving_vertices(vertices_to_drag);

        uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag([], vertices_to_drag, "move_vertex"));

        uid_in_dragging_mode = true;
        start_move_vertices(vertices_to_drag);
      }
      if (origin && direction)
        move_vertices(vertices_to_drag, origin, direction);
      //project_vertices(vertices_to_drag);
    }
    else if ( nsel_v == 0 && nsel_e == 1 && nsel_f == 0 && uil_is_vertex_of_edge(vertexid, uid_selected_edges.getItemAtIndex(0).id)) {
      // Dragging vertex on edge
      App.state.dragging_type = "vertex_on_edge";
      uid_drag_edge_for_vertex = uid_selected_edges.getItemAtIndex(0);

      if (uid_state.in_first_drag_step) {
        uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag([], [vertexid], "move_vertex_on_edge"));
        start_move_vertex_on_edge(vertexid, uid_drag_edge_for_vertex.id);
      }
      if (origin && direction)
        move_vertex_on_edge(vertexid, uid_drag_edge_for_vertex.id, origin, direction);
    }
  }
}

export function uil_end_drag_vertex(vertexid) {
  //console_log("End drag");
  hide_debug_objs(true);
  clear_small_balls();
  // Create list of all vertices to drag together
  var vertices_to_drag = [vertexid];
  if (uid_additional_drag_vertices.length > 0)
    vertices_to_drag.push(...uid_additional_drag_vertices);
  // Remove duplicates
  // Just explaining why this is needed: In the even when the user is performing a drag vertex with the manipulator,
  // we use the array uid_additional_drag_vertices for all of the selected vertices, including the main drag vertex.
  // So here it may end up in the array twice.
  vertices_to_drag = [...new Set(vertices_to_drag)];
  end_move_vertices(vertices_to_drag);
  uid_in_dragging_mode = false;
  detach_working_plane_for_dragging();
  uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag([], [], "finished_drag"));

  // XXX DEBUG
  /*
  var b = new THREE.Vector3();
  var d = new THREE.Vector3();
  var n = new THREE.Vector3();
  var wv_r = new WrappedValue(0);
  var wv_f = new WrappedValue(0);
  uil_get_face_geometry(72, b, n, wv_r, d, wv_f);
  var look_vector = get_camera_look_vector(uid_camera).normalize();
  var a = n.angleTo(look_vector);
  display_message_to_user(LOG.INFO, "Flatness: " + wv_f.v);
  display_message_to_user(LOG.INFO, "Look angle: " + a * 180 / Math.PI);
  */

  /*
  if (uid_state.smoothen_edges_post_drag > 0) {
    smoothen_edge_chains_for_curvature_combs(uil_get_edges_of_vertex(vertexid), REGULAR_STRENGTH);
  }
  */

  /*
  if (uid_state.live_fairing) {
    // See whether we must also do fairing with the adjacent edges
    suppress_undo_tracking();
    suppress_animate();

    // Try all edges that are attached to the vertex to form chains.
    var es = uil_get_edges_of_vertex(vertexid);
    var edge_chains = [];
    es.forEach(function(eid){
      var ec = form_smooth_edge_chain(eid);
      if (ec.length > 1 && uil_can_smoothen_edges_as_one(ec) ) {
        var edge_chain = [... ec];
        var is_new = true;
        edge_chains.forEach(function(ec1){
          if (ec1.indexOf(edge_chain[0]) != -1)
            is_new = false;
        });
        if (is_new) {
          edge_chains.push(edge_chain);
        }
      }
    });

    uil_fix_objects([vertexid]);

    edge_chains.forEach(function(edge_chain){
      if (edge_chain.length > 1) {
        var ids_of_edges_to_smoothen = [];
        var wv_is_open_list = new WrappedValue();
        var res = uil_listify_edges(edge_chain, wv_is_open_list, ids_of_edges_to_smoothen);
        if (res)
        uil_smoothen_edges_as_one(ids_of_edges_to_smoothen, wv_is_open_list.v, null, false);
        else
        display_message_to_user(LOG.WARN, "Error in smoothening chain of edges.");
      }
    });

    uil_unfix_objects([vertexid]);
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
  }
  */
}


export function uil_translate_edge_list(
    edge_list,
    uid_manip_previous_raycaster_origin,
    uid_manip_previous_raycaster_direction,
    uid_manip_current_raycaster_origin,
    uid_manip_current_raycaster_direction,
) {
  suppress_animate();
  App.state.dragging_type = "edge_list";
  if (uid_state.in_first_drag_step) {
    uid_store_and_deselect_all_objects();
    // Set number of triangles

    // Display coordinates if necessary
    if (moving_coordinates_visible.value) {
      var vertices_of_edges = [];
      edge_list.forEach(function(eid){
        var v0 = uil_get_vertices_of_edge(eid)[0];
        var v1 = uil_get_vertices_of_edge(eid)[1];
        if (vertices_of_edges.indexOf(v0) == -1)
          vertices_of_edges.push(v0);
        if (vertices_of_edges.indexOf(v1) == -1)
          vertices_of_edges.push(v1);
      });
      indicate_moving_vertices(vertices_of_edges);
    }

    uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag(edge_list, [], "translate_edge_list"));
    start_move_edge_list(edge_list);
    uid_reselect_all_stored_objects();
  }
  uid_state.in_first_drag_step = false;

  var b = new THREE.Vector3(0, 0, 0);
  var d = new THREE.Vector3(1, 0, 0);
  var translation_vector = vector3.sub(
    uid_manip_current_raycaster_origin,
    uid_manip_previous_raycaster_origin
  );

  if (translation_vector)
    move_edge_list(edge_list, translation_vector, 0, b, d);
  uid_reselect_all_stored_objects();
  unsuppress_animate_if_ready();
}

export function uil_rotate_edge_list_around_axis( edge_list, axis_center, axis_direction, angle ) {
  if (edge_list.length == 0) {
    display_message_to_user(LOG.WARN, "You need to select at least one edge");
    return;
  }
  suppress_animate();
  App.state.dragging_type = "edge_list";

  // If the angle is < 0, we invert the axis and the sign of the angle
  if (angle <0) {
    angle = -angle;
    axis_direction.multiplyScalar(-1);
  }
  if (uid_state.in_first_drag_step)  {
    // Display coordinates if necessary
    if (moving_coordinates_visible.value) {
      var vertices_of_edges = [];
      edge_list.forEach(function(eid){
        var v0 = uil_get_vertices_of_edge(eid)[0];
        var v1 = uil_get_vertices_of_edge(eid)[1];
        if (vertices_of_edges.indexOf(v0) == -1)
          vertices_of_edges.push(v0);
        if (vertices_of_edges.indexOf(v0) == -1)
          vertices_of_edges.push(v1);
      });
      indicate_moving_vertices(vertices_of_edges);
    }

    // Set number of triangles
    uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag(edge_list, [], "rotate_edge_list"));
    start_move_edge_list(edge_list);
    uid_reselect_all_stored_objects();
  }
  uid_state.in_first_drag_step = false;
  var t = new THREE.Vector3(0, 0, 0);

  // If angle is almost 180 degrees, make it exactly 180 to avoid exceeding core specs due to rounding.
  if (maths.proximate(angle, Math.PI, 0.000001)) {
    if (axis_center && axis_direction) {
      move_edge_list(edge_list, t, Math.PI/2.0, axis_center, axis_direction);
      move_edge_list(edge_list, t, Math.PI/2.0, axis_center, axis_direction);
    }
  }
  else {
    if (axis_center && axis_direction) {
      move_edge_list(edge_list, t, angle, axis_center, axis_direction);
    }
  }
  uid_reselect_all_stored_objects();
  unsuppress_animate_if_ready();
}

export function uil_directly_move_edge_list(edge_ids, translation_vector, rotation_angle, rotation_axis_base, rotation_axis_direction) {
  suppress_animate();
  if (translation_vector && rotation_axis_base && rotation_axis_direction)
    move_edge_list( edge_ids, translation_vector, rotation_angle, rotation_axis_base, rotation_axis_direction );
  unsuppress_animate_if_ready();
}

export function uil_drag_edge(edgeid, origin, direction) {
  if (App.state.editing_reference_sketches)
    return;

  hide_debug_objs(true);
  suppress_animate();
  if (uil_is_object_fixed(edgeid)) {
    display_message_to_user(LOG.WARN, "Edge is fixed, cannot deform it");
  }
  else {
    // Deformation of the edge
    App.state.dragging_type = "edge";
    if (uid_state.in_first_drag_step) {
      uid_in_dragging_mode = true;
      uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag([edgeid], [], "deform_edge"));
      start_deform_edge(edgeid);
    }
    if (origin && direction)
      deform_edge(edgeid, origin, direction);
  }
  unsuppress_animate_if_ready();
}

export function uil_end_drag_edge(edgeid) {
  //console_log("End drag");
  hide_debug_objs(true);
  //suppress_animate();
  uid_in_dragging_mode = false;
  end_deform_edge(edgeid);
  detach_working_plane_for_dragging();
  uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag([], [], "finished_drag"));


  /*
  if (uid_state.smoothen_edges_post_drag > 0) {
    smoothen_edge_chains_for_curvature_combs([edgeid], SOFT_STRENGTH);
  }
  */

  /*
  if (uid_state.live_fairing) {
    suppress_undo_tracking();
    // See whether we must also do fairing with the adjacent edges
    var edge_chain = form_smooth_edge_chain(edgeid);
    if ( edge_chain.length > 1 && uil_can_smoothen_edges_as_one(edge_chain) ) {
      var ids_of_edges_to_smoothen = [];
      var wv_is_open_list = new WrappedValue();
      var res = uil_listify_edges(edge_chain, wv_is_open_list, ids_of_edges_to_smoothen);
      if (res)
        uil_smoothen_edges_as_one(ids_of_edges_to_smoothen, wv_is_open_list.v);
      else
        display_message_to_user(LOG.WARN, "Error in smoothening chain of edges.");
    }
    unsuppress_undo_tracking_if_ready(false);
  }
  */

  //unsuppress_animate_if_ready();
}

export function uil_get_edge_list_geometry( edge_ids, is_high_res, centre_coords, norm_coords, wv_radius, characteristic_dir_coords ) {
  return get_edge_list_geometry(edge_ids, is_high_res, centre_coords, norm_coords, wv_radius, characteristic_dir_coords);
}

export function uil_get_face_geometry( face_id, centre_coords, norm_coords, wv_radius, characteristic_dir_coords, wv_flatness ) {
  return get_face_geometry(face_id, centre_coords, norm_coords, wv_radius, characteristic_dir_coords, wv_flatness);
}

export function uil_listify_edges(input_edge_ids, wv_is_open_list, output_edge_ids) {
  if (input_edge_ids.length == 1) {
    output_edge_ids. push(input_edge_ids[0]);
    wv_is_open_list.v = true;
    return true;
  }
  return listify_edges(input_edge_ids, wv_is_open_list, output_edge_ids);
}

export function uil_get_face_connected_component_info(input_face_ids, wv_is_one_cc, output_boundary_edge_ids, output_boundary_vertex_ids) {
  // First find number of edges and number of vertices contained in list of faces
  var input_edge_ids = [];
  var input_vertex_ids = [];
  input_face_ids.forEach(function(fid){
    var es = uil_get_edges_of_face(fid);
    input_edge_ids = input_edge_ids.concat(es);
    var vs = vertices_of_face(fid);
    input_vertex_ids = input_vertex_ids.concat(vs);
  });
  // Remove duplicates
  input_edge_ids = [...new Set(input_edge_ids)];
  input_vertex_ids = [...new Set(input_vertex_ids)];

  return get_face_connected_component_info(input_face_ids, input_edge_ids, input_vertex_ids,
     wv_is_one_cc, output_boundary_edge_ids, output_boundary_vertex_ids);
}

export function uil_get_face_connected_components(input_face_ids) {
  var output_connected_component_array = new Array();
  var res = get_face_connected_components(input_face_ids, output_connected_component_array);
  if (res)
    return output_connected_component_array;
  else
    return null;
}


export function uil_move_vertex_to_coords( vertex_id, x, y, z) {
  // If we're already there, don't do anything
  var v1 = new THREE.Vector3();
  var v2 = new THREE.Vector3(x, y, z);
  var eps = 0.00001;
  v1.copy(uid_vertices.getItem(vertex_id).object3d.position);
  if (vector3.point_point_distance(v1, v2) < eps) {
    // console_log("skipping uil_move_vertex_to_coords for vertex ", vertex_id);
    return;
  }
  suppress_animate();
  move_vertex_to_coords(vertex_id, x, y, z);
  unsuppress_animate_if_ready();
}


export function uil_get_face_superedges(face_id) {
  var nse = get_sedge_num_of_face(face_id);
  var ne = get_edge_num_of_face(face_id);

  if (nse == -1 || ne == -1) {
    console_log_xml("ERROR with superedges");
    return null;
  }

  var sedges_hash = new HashTable();
  var sedges = get_face_superedges(face_id, ne, nse);

  if (sedges) {
    var i = 0;
    while (i < sedges.length) {
      var ar = [];
      var seid = sedges[i++];
      var nedges = sedges[i++];
      var j = 0;
      while (j < nedges) {
        ar[j] = sedges[i];
        i++;
        j++;
      }
      sedges_hash.setItem(seid, ar);
    }
    return(sedges_hash);
  }
  else {
    console_log_xml("ERROR with superedges 2");
    return null;
  }
}

export function uil_get_face_crosshair_at_params(face_id, u, v, returned_edge_ids, returned_coords) {
  if (u >= 0 && v >= 0)
    return get_face_crosshair_at_params(face_id, u, v, returned_edge_ids, returned_coords);
  else
    return 0;
}

export function uil_start_scale_edge_list(scale_edge_objects) {
  if (scale_edge_objects.length == 0) {
    display_message_to_user(LOG.WARN, "You need to select at least one edge");
    return;
  }
  App.state.dragging_type = "scale_edge_list";
  uid_store_and_deselect_all_objects();

  // Display coordinates if necessary
  if (moving_coordinates_visible.value) {
    var vertices_of_edges = [];
    scale_edge_objects.forEach(function(eid){
      var v0 = uil_get_vertices_of_edge(eid)[0];
      var v1 = uil_get_vertices_of_edge(eid)[1];
      if (vertices_of_edges.indexOf(v0) == -1)
        vertices_of_edges.push(v0);
      if (vertices_of_edges.indexOf(v0) == -1)
        vertices_of_edges.push(v1);
    });
    indicate_moving_vertices(vertices_of_edges);
  }

  uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag(scale_edge_objects, [], "scale_edge_list"));
  start_scale_edge_list(scale_edge_objects);
  uid_reselect_all_stored_objects();
}

export function uil_scale_edge_list(scale_edge_objects, scale_center, scale_factor, use_axis, axis) {
  if (scale_center && scale_factor)
    scale_edge_list(scale_edge_objects, scale_center, scale_factor, use_axis, axis);
  uid_reselect_all_stored_objects();
}

export function uil_end_scale_edge_list(scale_edge_objects) {
  end_scale_edge_list(scale_edge_objects);
  App.state.dragging_type = "none";
  uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag(scale_edge_objects, [], "finished_drag"));
  uid_reselect_all_stored_objects();
  request_setting_button_states();
}

export function uil_start_offset_face(face_id, vertex_id, point_coordinates) {
  return start_offset_face(face_id, vertex_id, point_coordinates);
}

export function uil_offset_face_drag_step(face_id, vertex_id, point_coordinates) {
  return offset_face_drag_step(face_id, vertex_id, point_coordinates);
}

export function uil_end_offset_face(face_id, vertex_id) {
  var res = end_offset_face(face_id, vertex_id);
  request_setting_button_states();
  return res;
}

export function uil_delete_selected_objects(modifier = false) {
  const selected_vertices = [...uid_selected_vertices.keys()];
  const selected_edges = [...uid_selected_edges.keys()];
  const selected_faces = [...uid_selected_faces.keys()];

  // If faces are selected, we ignore the edges and vertices
  if (selected_faces.length > 0) {
    var res = uil_delete_faces(selected_faces);
    return;
  }

  if (selected_vertices.length > 0 && selected_edges.length > 0)
    display_message_to_user(LOG.WARN, "Cannot simultaneously delete faces, edges and vertices. Try one object type at a time.");

  if (selected_vertices.length > 0)
    var res = uil_delete_vertices(selected_vertices, modifier);

  if (selected_edges.length > 0)
    var res = uil_delete_edges(selected_edges, modifier);
}

export function uil_delete_faces(faces_to_delete = []) {
  var res = 0;

  // First check that any faces to delete are all from the same body
  var body_id = 0;
  var all_same = true;
  faces_to_delete.forEach(function(id){
    var bid = uil_get_body_of_face(id);
    if (body_id == 0)
      body_id = bid;
    else if (body_id != bid)
      all_same = false;
  });
  if (all_same == false) {
    display_message_to_user(LOG.WARN, "Cannot simultaneously delete faces from different bodies. Try one body at a time.");
    return 0;
  }

  // Then check if objects to delete are fixed
  var fixed_found = false;
  faces_to_delete.forEach(function(id) {
    if (uil_is_object_fixed(id))
      fixed_found = true;
  });
  if (fixed_found) {
    display_message_to_user(LOG.WARN, "Cannot delete fixed objects. Please unfix them first.");
    return 0;
  }

  // Start deleting.
  suppress_undo_tracking();
  suppress_animate();
  uid_deselect_all_objects();

  // If we only have faces to delete then:
  // - If these are ALL the (visible) faces of the body, then we delete the entire body.
  // - Otherwise, we must make all of these faces invisible.
  var all_body_faces_selected = true;
  uid_faces.keys().forEach(function(fid){
    if (uil_is_object_visible(fid) && uil_get_body_of_face(fid) == body_id && faces_to_delete.indexOf(fid) == -1) {
      all_body_faces_selected = false;
    }
  });
  if (all_body_faces_selected) {
    uil_delete_body(body_id);
  }
  else {
    display_message_to_user(LOG.INFO, "Making the selected faces invisible.");
    uil_make_faces_invisible(faces_to_delete, false);
  }
  res = 1;

  // Wrap up
  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);

  if (App.state.editing_reference_sketches && number_of_non_deleted_reference_sketches() == 0) {
    // Exit editing sketches state
    stop_editing_sketches();
  }
  return res;
}

export function uil_delete_edges(edges_to_delete = [], also_delete_vertices = false) {
  var res = 0;

  // If we're editing reference sketches, return
  if (App.state.editing_reference_sketches)
    return 0;

  // Then check if objects to delete are fixed
  var fixed_found = false;
  edges_to_delete.forEach(function(id) {
    if (uil_is_object_fixed(id))
      fixed_found = true;
  });
  if (fixed_found) {
    display_message_to_user(LOG.WARN, "Cannot delete fixed objects. Please unfix them first.");
    return 0;
  }

  // Start deleting.
  suppress_undo_tracking();
  suppress_animate();
  uid_deselect_all_objects();

  // To delete edges, we extract the faces attached to each and delete them
  var edge_vertices = [];
  edges_to_delete.forEach(function(eid) {
    // Make a note of the vertices of this edge
    push_nodups(uil_get_vertices_of_edge(eid)[0], edge_vertices);
    push_nodups(uil_get_vertices_of_edge(eid)[1], edge_vertices);
    var f_array = uil_get_faces_of_edge(eid);
    res = uil_directly_delete_faces(f_array);
  });

  // Also delete vertices if requested
  if (also_delete_vertices) {
    var vertices_to_delete = [];
    edge_vertices.forEach(function(vid){
      var es = uil_get_edges_of_vertex(vid);
      // Remove invisible edges
      if (es && es.length > 0) {
        for (var i = es.length-1; i >= 0; i--) {
          if (!uil_is_object_visible(es[i]))
            es.splice(i, 1);
        }
        if (es.length == 2) {
          push_nodups(vid, vertices_to_delete);
        }
      }
    });
    uil_delete_vertices(vertices_to_delete, false);
  }

  // Wrap up
  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  return res;
}

export function uil_delete_vertices(vertices_to_delete = [], no_geometry_change = false) {
  var res = 0;

  // If we're editing reference sketches, return
  if (App.state.editing_reference_sketches)
    return;

  // Then check if objects to delete are fixed
  var fixed_found = false;
  vertices_to_delete.forEach(function(id) {
    if (uil_is_object_fixed(id))
      fixed_found = true;
  });
  if (fixed_found) {
    display_message_to_user(LOG.WARN, "Cannot delete fixed objects. Please unfix them first.");
    return 0;
  }

  // Start deleting.
  suppress_undo_tracking();
  suppress_animate();
  uid_deselect_all_objects();

  res = 1;

  // Before deleting the vertex we need to take care of one thing. If there are any invisible edges on the vertex,
  // we have to delete them first. Otherwise the user won't know why the vertex cannot be deleted.
  vertices_to_delete.forEach(function(vid) {
    if (isVertex(vid)) {
      var e_array = uil_get_edges_of_vertex(vid);
      e_array.forEach(function(eid){
        // Check in case eid has already been removed
        if (isEdge(eid)) {
          if (!uil_is_object_visible(eid)) {
            var f_array = uil_get_faces_of_edge(eid);
            // Remove any faces that may no longer exist
            for (var i = f_array.length-1; i >= 0; i--) {
              if (!uid_faces.getItem(f_array[i]))
                f_array.splice(i, 1);
            }
            res = uil_directly_delete_faces(f_array);
          }
        }
      });
    }
  });

  // Now delete the actual vertices
  vertices_to_delete.forEach(function(vid) {
    if (isVertex(vid)) {
      var eids = uil_get_edges_of_vertex(vid);
      if (eids.length == 2) {
        // This is a 2-vertex, delete the vertex.
        // If no_geometry_change is set, don't modify edge geometry
        // Check if both edges are in symmetry set
        if ((symmetry_edges_list_first.includes(eids[0]) || symmetry_edges_list_second.includes(eids[1])) &&
            (symmetry_edges_list_first.includes(eids[1]) || symmetry_edges_list_second.includes(eids[1])))
          delete_vertex_on_symmetry_edges(vid);
        else
          delete_vertex(vid, no_geometry_change)
      }
      else {
        // Delete the vertex edges instead.
        // Before we do that, if the vertex is on the border with an invisible part of the body we will turn invisible all the faces that it lies on.
        var veids = uil_get_edges_of_vertex(vid).filter(eid => uil_is_object_visible(eid));
        if (veids.length > 2) {
          let fids = faces_of_vertex(vid).filter(fid => !uil_is_object_visible(fid));
          if (fids.length > 0) {
            faces_of_vertex(vid).forEach(function(fid){
              uil_make_faces_invisible([fid]);
            });
          }
        }

        // Then proceed with the deleting
        eids.forEach(function(eid) {
          // Check in case eid has already been removed
          if (isEdge(eid)) {
            var f_array = uil_get_faces_of_edge(eid);
            // Remove any faces that may no longer exist
            for (var i = f_array.length-1; i >= 0; i--) {
              if (!uid_faces.getItem(f_array[i]))
                f_array.splice(i, 1);
            }
            res = uil_directly_delete_faces(f_array);
          }
        });
      }
    }
  });

  // Wrap up
  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  return res;
}

function delete_vertex_on_symmetry_edges(vid, no_geometry_change) {
  // First we check if there is another vertex sitting between the edges that are symmetric to these two edges,
  // and that is a 2-vertex
  var eids = uil_get_edges_of_vertex(vid);
  var eid1 = eids[0];
  var eid2 = eids[1];
  var seid1 = 0;
  var seid2 = 0;

  if (symmetry_edges_list_first.includes(eid1))
    seid1 = symmetry_edges_list_second[symmetry_edges_list_first.indexOf(eid1)];
  else if (symmetry_edges_list_second.includes(eid1))
    seid1 = symmetry_edges_list_first[symmetry_edges_list_second.indexOf(eid1)];

  if (symmetry_edges_list_first.includes(eid2))
    seid2 = symmetry_edges_list_second[symmetry_edges_list_first.indexOf(eid2)];
  else if (symmetry_edges_list_second.includes(eid2))
    seid2 = symmetry_edges_list_first[symmetry_edges_list_second.indexOf(eid2)];

  if (seid1 == 0 || seid2 == 0)
    // Something went wrong
    return;

  var svid = find_edge_shared_vertex(seid1, seid2);
  if (svid > 0 && uil_get_edges_of_vertex(svid).length != 2) {
    // corresponding vertex is not 2-vertex, delete only the one
    delete_vertex(vid, no_geometry_change)
    uid_unhighlight_all_objects();
    return;
  }

  // Take note of current edges
  const previous_edge_array = [... uid_edges.keys()];

  // Remove both vertices
  delete_vertex(vid, no_geometry_change)
  delete_vertex(svid, no_geometry_change)

  // Find new edges
  const new_edges = uid_edges.keys().filter(eid => !previous_edge_array.includes(eid));
  new_edges.forEach(function(eid){
    console.log(eid);
  });

  if (new_edges.length != 2)
    // Something went wrong
    return;

  // Select edges to add them to symmetry set
  add_to_symmetry_set(new_edges, []);
  uid_unhighlight_all_objects();
}

export function uil_directly_delete_faces(face_ids) {

    // Check whether we have to delete sketches that are attached to these faces.
    if (number_of_non_deleted_reference_sketches() > 0) {
      face_ids.forEach(function(fid) {
        // Check if this face is a sketch face
        if (uil_is_object_visible(fid) && face_is_for_sketch(fid))
          delete_sketch_of_face(fid);
      });
    }

    return delete_faces(face_ids);
}

export function uil_delete_body(body_id) {
  console_log("All faces of the body are being deleted. Deleting the entire body.");
  uid_deselect_all_objects();

  // Check if the faces of the body carry sketches, in which case delete those too.
  if (number_of_non_deleted_reference_sketches() > 0) {
    uid_faces.keys().forEach(function(fid) {
      // Check if this face is a sketch face
      if (uil_get_body_of_face(fid) == body_id && uil_is_object_visible(fid) && face_is_for_sketch(fid))
        delete_sketch_of_face(fid);
    });
  }

  return delete_body(body_id);
}

export function uil_merge_faces(face_ids, split_merged_faces = true, v1id_for_edge = 0, v2id_for_edge = 0) {
  var cannot_proceed_due_to_fixed_objects = false;
  face_ids.forEach(function(fid){
    if (uil_is_object_fixed(fid))
      cannot_proceed_due_to_fixed_objects = true;
  });

  // Now check that no edges internal to the selected faces are fixed. These edges will be referenced by more than one face.
  var edges_hash;
  if (!cannot_proceed_due_to_fixed_objects) {
    edges_hash = new HashTable();
    face_ids.forEach(function(fid){
      var l = uil_get_edges_of_face(fid);
      l.forEach(function(eid){
        var n = edges_hash.getItem(eid) || 0;
        edges_hash.setItem(eid, n+1);
      });
    });
    edges_hash.keys().forEach(function(eid){
      if (edges_hash.getItem(eid) > 1 && uil_is_object_fixed(eid))
        cannot_proceed_due_to_fixed_objects = true;
    });
  }

  // Now check that no vertices internal to the selected faces are fixed. These vertices will have faces that are all in the face_ids array
  if (!cannot_proceed_due_to_fixed_objects) {
    var border_fixed_vertices = [];
    var all_fixed_vertices = [];
    edges_hash.keys().forEach(function(eid){
      var vs = uil_get_vertices_of_edge(eid);
      if (uil_is_object_fixed(vs[0]) && all_fixed_vertices.indexOf(vs[0]) == -1)
        all_fixed_vertices.push(vs[0])
      if (uil_is_object_fixed(vs[1]) && all_fixed_vertices.indexOf(vs[1]) == -1)
        all_fixed_vertices.push(vs[1])
      if (edges_hash.getItem(eid) == 1) {
        if (uil_is_object_fixed(vs[0]) && border_fixed_vertices.indexOf(vs[0]) == -1)
          border_fixed_vertices.push(vs[0])
        if (uil_is_object_fixed(vs[1]) && border_fixed_vertices.indexOf(vs[1]) == -1)
        border_fixed_vertices.push(vs[1])
      }
    });
    all_fixed_vertices.forEach(function(vid){
      if (border_fixed_vertices.indexOf(vid) == -1)
        cannot_proceed_due_to_fixed_objects = true;
    });
  }

  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot dissolve due to fixed objects.");
    return 0;
  }

  // We will now proceed to form independent connected components, for visible and invisible faces, and merge within each
  // independent component.
  var visible_face_ids = [];
  var invisible_face_ids = [];
  face_ids.forEach(function(fid){
    if (uil_is_object_visible(fid))
      push_nodups(fid, visible_face_ids);
    else
      push_nodups(fid, invisible_face_ids);
  });

  var invisible_connected_components = [];
  var visible_connected_components = [];
  if (invisible_face_ids.length > 0)
    invisible_connected_components = uil_get_face_connected_components(invisible_face_ids);
  if (visible_face_ids.length > 0)
    visible_connected_components = uil_get_face_connected_components(visible_face_ids) || [];

  var merged_faces = [];
  // Now merge each one of the connected components
  invisible_connected_components.forEach(function(face_group) {
    var new_merged_faces = merge_face_connected_component(face_group, false, split_merged_faces, v1id_for_edge, v2id_for_edge);
    if (new_merged_faces.length > 0) {
      new_merged_faces.forEach(function(fid){
        push_nodups(fid, merged_faces);
      });
    }
  });

  visible_connected_components.forEach(function(face_group) {
    var new_merged_faces = merge_face_connected_component(face_group, true, split_merged_faces, v1id_for_edge, v2id_for_edge);
    if (new_merged_faces.length > 0) {
      new_merged_faces.forEach(function(fid){
        push_nodups(fid, merged_faces);
      });
    }
  });

  return merged_faces;
}

export function merge_face_connected_component(face_ids, is_visible, split_new_face, v1id_for_edge = 0, v2id_for_edge = 0) {

  suppress_undo_tracking();
  suppress_animate();
  suppress_ui_messages(); // So we don't repeatedly warn the user about removing tangencies etc

  var make_new_edges_linear = false;
  var merged_face_id;
  var removed_tangencies = [];

  // First we try a direct delete_faces
  if (face_ids.length == 1) {
    merged_face_id = face_ids[0];
  }
  else {
    uil_set_auto_add_tangencies(false);
    merged_face_id = uil_directly_delete_faces(face_ids);
    uil_set_auto_add_tangencies(true);
    if (merged_face_id > 0) {
      if (is_visible)
        uil_make_faces_visible([merged_face_id]);
      else
        uil_make_faces_invisible([merged_face_id], false);
    }
    //else if (split_new_face) {
    // NOTE: I wanted to make the above change to avoid weird situations where we try to hard to merge a face
    // and result in collapsed stuff, but it had bad side-effects, so I reverted to the simple "else" below.
    else {
      // Failed to delete faces. Will need to remove tangencies all around the border edges and try again.

      var border_edges = get_list_of_edges_around_faces(face_ids);
      var listified_border_edges = [];
      var wv_is_open_list = new WrappedValue();
      var edge_lists = get_listified_lists_of_connected_edges_in_edge_list(border_edges, true);
      edge_lists.forEach(function(listified_border_edges_obj) {
        var listified_border_edges = listified_border_edges_obj.edges; // XXX Should check if closed loop?

        // Remove all tangencies, but keep a list.
        for (var i = 0; i < listified_border_edges.length-1; i++) {
          if (uil_is_joint_smooth(listified_border_edges[i], listified_border_edges[i+1]) == 1) {
            removed_tangencies.push([listified_border_edges[i], listified_border_edges[i+1]]);
            var res = uil_remove_joint_smoothness([[listified_border_edges[i], listified_border_edges[i+1]]],
                                                  true, true);
          }
          //console.log("i:", i);
        }
        if (uil_is_joint_smooth(listified_border_edges[listified_border_edges.length-1], listified_border_edges[0]) == 1) {
          removed_tangencies.push([listified_border_edges[listified_border_edges.length-1], listified_border_edges[0]]);
          var res = uil_remove_joint_smoothness([[listified_border_edges[listified_border_edges.length-1], listified_border_edges[0]]],
                                                true, true);
        }
      });

      uil_set_auto_add_tangencies(false);
      var merged_face_id = uil_directly_delete_faces(face_ids);
      uil_set_auto_add_tangencies(true);
      if (merged_face_id > 0) {
        if (is_visible)
          uil_make_faces_visible([merged_face_id]);
        else
          uil_make_faces_invisible([merged_face_id], false);
      }

      // Must split new face in order to add back tangencies.
      split_new_face = true;
      make_new_edges_linear = true;
    }
    /*
    else {
      // Fail
      unsuppress_ui_messages_if_ready();
      unsuppress_animate_if_ready();
      unsuppress_undo_tracking_if_ready(false);
      display_message_to_user(LOG.WARN, "Failed to dissolve faces into one. Cause may be related to the 3-vertex limitation. Perhaps try removing some edge-tangencies around the faces and repeat. [code E3d]");
      return [];
    }
    */
  }

  var new_faces = [];

  if (merged_face_id == 0) {
    unsuppress_ui_messages_if_ready();
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    return new_faces;
  }

  if (!split_new_face && v1id_for_edge > 0 && v2id_for_edge > 0) {
    // Check if that edge is already there
    var new_edge_id = 0;
    uid_edges.keys().forEach(function(eid){
      var vs = uil_get_vertices_of_edge(eid);
      if ((vs[0] == v1id_for_edge && vs[1] == v2id_for_edge) || (vs[1] == v1id_for_edge && vs[0] == v2id_for_edge))
        new_edge_id = eid;
    });

    // If not, attempt to add that edge
    if (new_edge_id == 0) {
      var make_invisible = false;
      if (!uil_is_object_visible(merged_face_id)) {
        uil_make_faces_visible([merged_face_id]);
        make_invisible = true;
      }

      var vs = vertices_of_face(merged_face_id);
      if (vs.indexOf(v1id_for_edge) == -1 || vs.indexOf(v2id_for_edge)== -1) {
        // At least one of the two vertices does not lie on the merged face, this is hopeless, fail.
        unsuppress_ui_messages_if_ready();
        unsuppress_animate_if_ready();
        unsuppress_undo_tracking_if_ready(false);
        return new_faces;
      }

      // Now we want to add an edge between the two vertices. But first we must make sure they don't coincide! If they do, we will move
      // the first vertex slightly towards the other end of its visible edge.
      if (v1id_for_edge > 0 && v2id_for_edge > 0 &&
        vector3.point_point_distance(
              uid_vertices.getItem(v1id_for_edge).object3d.position,
              uid_vertices.getItem(v2id_for_edge).object3d.position) < 0.0001) {
        let new_coords = new THREE.Vector3().copy(uid_vertices.getItem(v1id_for_edge).object3d.position);
        let es = uil_get_edges_of_vertex(v1id_for_edge);
        es.forEach(function(eid){
          if (uil_is_object_visible(eid)) {
            let vs = uil_get_vertices_of_edge(eid);
            let vdist = vs[0] == v1id_for_edge ? vs[1] : vs[0];
            let c0 = new THREE.Vector3().copy(uid_vertices.getItem(v1id_for_edge).object3d.position);
            let c1 = new THREE.Vector3().copy(uid_vertices.getItem(vdist).object3d.position);
            new_coords.addVectors(new_coords.multiplyScalar(0.98), c1.multiplyScalar(0.02));
          }
        });
        uil_move_vertex_to_coords(v1id_for_edge, new_coords.x, new_coords.y, new_coords.z);
      }

      var new_edge_id = add_edge_between_two_vertices_on_face(v1id_for_edge, v2id_for_edge, merged_face_id, null, false);
      if (new_edge_id > 0) {
        uil_set_linearity_of_edges([new_edge_id], true);
        var fs = uil_get_faces_of_edge(new_edge_id);
        new_faces.push(fs[0]);
        new_faces.push(fs[1]);
        if (new_faces.indexOf(merged_face_id) != -1)
          new_faces.splice(new_faces.indexOf(merged_face_id), 1);
        if (make_invisible)
          uil_make_faces_invisible(new_faces, false);
      }
    }

    if (new_edge_id == 0 && removed_tangencies.length == 0 && listified_border_edges) {
      // Remove tangencies and try again
      for (var i = 0; i < listified_border_edges.length-1; i++) {
        if (uil_is_joint_smooth(listified_border_edges[i], listified_border_edges[i+1]) == 1) {
          removed_tangencies.push([listified_border_edges[i], listified_border_edges[i+1]]);
          var res = uil_remove_joint_smoothness([[listified_border_edges[i], listified_border_edges[i+1]]],
                                                true, true);

        }
        //console.log("i:", i);
      }
      if (uil_is_joint_smooth(listified_border_edges[listified_border_edges.length-1], listified_border_edges[0]) == 1) {
        removed_tangencies.push([listified_border_edges[listified_border_edges.length-1], listified_border_edges[0]]);
        var res = uil_remove_joint_smoothness([[listified_border_edges[listified_border_edges.length-1], listified_border_edges[0]]],
                                              true, true);

      }
      var new_edge_id = add_edge_between_two_vertices_on_face(v1id_for_edge, v2id_for_edge, merged_face_id, null, false);
      if (new_edge_id > 0) {
        uil_set_linearity_of_edges([new_edge_id], true);
        var fs = uil_get_faces_of_edge(new_edge_id);
        new_faces.push(fs[0]);
        new_faces.push(fs[1]);
        if (new_faces.indexOf(merged_face_id) != -1)
          new_faces.splice(new_faces.indexOf(merged_face_id), 1);
        if (make_invisible)
          uil_make_faces_invisible(new_faces, false);
      }
    }

    if (new_edge_id == 0) {
      // Must split new face in order to add back tangencies.
      split_new_face = true;
      make_new_edges_linear = true;
      if (make_invisible)
        uil_make_faces_invisible([merged_face_id], false);
    }
  }

  if (split_new_face) {
    // Make the face visible in order to be able to split it.
    var make_invisible = false;
    if (!uil_is_object_visible(merged_face_id)) {
      uil_make_faces_visible([merged_face_id]);
      make_invisible = true;
    }
    new_faces = split_face_into_triangles(merged_face_id, make_invisible, make_new_edges_linear, v1id_for_edge, v2id_for_edge, false, []);
  }
  else if (isFace(merged_face_id)) {
    new_faces.push(merged_face_id);
  }

  // Add back tangencies that were removed. In the process, we may find out that we need to split some faces.
  var faces_to_split = can_add_back_removed_tangency_flags(removed_tangencies);

  uil_make_faces_visible(faces_to_split);
  faces_to_split.forEach(function(fid){
    split_face_into_triangles(fid,make_invisible, make_new_edges_linear, 0, 0, false, []);
  });

  add_back_removed_tangency_flags(removed_tangencies);

  unsuppress_ui_messages_if_ready();
  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);

  return new_faces;
}

export function can_add_back_removed_tangency_flags(removed_tangencies) {
  // Check whether we can add back all the tangency flags, or if we need to first split some faces.
  var faces_to_split = [];
  if (removed_tangencies.length == 0)
    return faces_to_split;

  var affected_faces = [];

  removed_tangencies.forEach(function(tangency_pair) {
    push_nodups(uil_get_faces_of_edge(tangency_pair[0])[0], affected_faces);
    push_nodups(uil_get_faces_of_edge(tangency_pair[0])[1], affected_faces);
    push_nodups(uil_get_faces_of_edge(tangency_pair[1])[0], affected_faces);
    push_nodups(uil_get_faces_of_edge(tangency_pair[1])[1], affected_faces);
  });

  affected_faces.forEach(function(fid){
    var border_edges = uil_get_edges_of_face(fid);
    var listified_border_edges = [];
    var wv_is_open_list = new WrappedValue();
    var res = uil_listify_edges(border_edges, wv_is_open_list, listified_border_edges);

    var currently_smooth_joints = [];
    var currently_nonsmooth_joints = [];
    var edge_pair_tangencies_to_be_added = [];

    // Add first edge again at the end of the array, just to make the loop simpler
    listified_border_edges.push(listified_border_edges[0]);
    for (var i = 0; i < listified_border_edges.length-1; i++) {
      var e1 = listified_border_edges[i];
      var e2 = listified_border_edges[i+1];
      if (uil_is_joint_smooth(e1, e2))
        currently_smooth_joints.push([e1,e2]);
      else
        currently_nonsmooth_joints.push([e1,e2]);
    }
    currently_nonsmooth_joints.forEach(function(j){
      removed_tangencies.forEach(function(t){
        if ((j[0] == t[0] && j[1] == t[1]) || (j[0] == t[1] && j[1] == t[0])) {
          edge_pair_tangencies_to_be_added.push(j);
        }
      });
    });

    // Now check what the status is with this face.
    if (currently_nonsmooth_joints.length - edge_pair_tangencies_to_be_added.length < 3) {
      console.log("Must split face " + fid);
      faces_to_split.push(fid);
    }
  });

  return faces_to_split;
}

export function add_back_removed_tangency_flags(removed_tangencies) {
  uil_add_joint_smoothness(removed_tangencies, global_smoothen_strength, true, false)
}

export function uil_get_visible_faces_of_edge(edge_id) {
  var face_ids = uil_get_faces_of_edge(edge_id);
  for (var i = face_ids.length-1; i >= 0; i--) {
    if (!uil_is_object_visible(face_ids[i]))
      face_ids.splice(i, 1);
  }
  return face_ids;
}

export function uil_get_faces_of_edge(edge_id) {
  var face_ids = get_faces_of_edge(edge_id);
  if (Array.isArray(face_ids))
    return face_ids;
  console_log_xml("Error in get_faces_of_edge");
  return [];
}

export function uil_get_vertices_of_edge(edge_id) {
  var vertex_ids = get_vertices_of_edge(edge_id);
  if (Array.isArray(vertex_ids))
    return vertex_ids;
  console_log_xml("Error in get_vertices_of_edge");
  return [];
}

export function uil_get_edges_of_face(face_id) {
  // First get the number of edges on this face
  const ne = get_edge_num_of_face(face_id);
  if (ne <= 0) {
    console_log_xml("Error in get_edge_num_of_face");
    return [];
  }

  // Then get list of edges
  var edge_ids = get_edges_of_face(face_id, ne);
  return edge_ids;
}

export function uil_get_visible_edges_of_vertex(vertex_id) {
  var edge_ids = uil_get_edges_of_vertex(vertex_id).filter( eid => uil_is_object_visible(eid));
  return edge_ids;
}

export function uil_get_edges_of_vertex(vertex_id) {
  // First get the number of edges on this vertex
  var ne = get_edge_num_of_vertex(vertex_id);
  if (ne <= 0) {
    console_log_xml("Error in get_edge_num_of_vertex");
    return [];
  }
  // Then get list of edges
  var edge_ids = get_edges_of_vertex(vertex_id, ne);
  if (Array.isArray(edge_ids))
    return edge_ids;
  console_log_xml("Error in get_edges_of_vertex");
  return [];
}

export function uil_drag_vertex_normal_manipulator(vid, direction, angle) {
  if (!uil_is_vertex_smooth(vid)) {
    display_message_to_user(LOG.WARN,  "You cannot rotate the normal of a sharp vertex. You can work with the edges instead.");
    return;
  }

  if (uid_state.in_first_drag_step)  {
    var res = start_rotate_vertex_normal(vid);
    if (!res) {
      return;
    }
  }
  uid_state.in_first_drag_step = false;
  App.state.dragging_type = "vertex_normal";
  if (direction)
    rotate_vertex_normal(vid, direction, angle);
}

export function uil_drag_edge_tangent_manipulator(eid, vid, direction, angle) {

  if (uil_is_edge_linear(eid))
    uil_set_linearity_of_edges([eid], false, false);
  var current_t = uil_get_edge_tangent_at_vertex(eid, vid);
  var t = vector3.rotate_vector_around_axis(current_t, direction, angle);
  return uil_set_edge_tangent_at_vertex(eid, vid, t);
}

export function uil_end_drag_manipulator() {
  suppress_animate();
  if (App.state.dragging_type == "vertex_normal") {
    var selected_object_id_array = new Array();
    collect_selected_object_ids_in_array(selected_object_id_array);
    var v_id = selected_object_id_array[0];
    end_rotate_vertex_normal(v_id);
    App.state.dragging_type = "none";
  }
  else if (App.state.dragging_type == "edge_list") {
    App.state.dragging_type = "none";
    end_move_edge_list(uid_rigid_move_manipulator.rigid_move_edge_object_ids);
    // Set number of triangles
    uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag([], [], "finished_drag"));
  }
  else if (App.state.dragging_type == "scale_edge_list") {
    App.state.dragging_type = "none";
    uid_end_scale_objects();
  }
  else if (App.state.dragging_type == "vertex") {
    // This happens when we use the rigid move manipulator to freely drag one vertex only.
    uil_end_drag_vertex(uid_rigid_move_manipulator.indicated_center_object_id);
    App.state.dragging_type = "none";
  }
  else {
    //...
  }
  unsuppress_animate_if_ready();
}

export function uil_get_ray_edge_intersection(edge_id, ray_base, ray_direction, is_high_res, wv_parameter) {
  return get_ray_edge_intersection(edge_id, ray_base, ray_direction, is_high_res, wv_parameter);
}

export function uil_get_face_ray_intersection(fid, ray_base, ray_direction, wv_u, wv_v, must_use_core) {
  // There are two options here. Either we intersect based on the Three.js raycaster mechanism,
  // or we call the core get_ray_edge_intersection.
  // The choice is set with the must_use_core input variable.
  // Note that if we don't use the core function, we can't calculate values for the u,v parameters,
  // and we set them both to 0. It's the responsibility of the calling function to be aware of this.

  // XXX HARD CODE USING CORE
  must_use_core = true;

  if (must_use_core) {
    return get_face_ray_intersection(fid, ray_base, ray_direction, wv_u, wv_v);
  }
  else {
    // Can't calculate the u,v parameter values at the intersection, so setting them to 0.
    wv_u.v = 0;
    wv_v.v = 0;
    // Set up raycaster and find intersections
    var farb = new THREE.Vector3();
    farb.copy(ray_direction);
    farb.multiplyScalar(-100);
    farb.add(ray_base);
    uid_raycaster.set(farb, ray_direction);
    var ray_intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
    var o = find_intersection_face_object_with_id(ray_intersects, fid);
    if (o) {
      var p = new THREE.Vector3();
      p.copy(o.point);
      return p;
    }
    else
      return null;
  }
}

export function uil_get_face_normal_at_edge_point(fid, eid, ray_base, ray_direction) {
  return get_face_normal_at_edge_point(fid, eid, ray_base, ray_direction);
}

export function uil_add_new_vertex(edge_id, ray_base, ray_direction) {
  if (edge_id == 0 || !ray_base || !ray_direction) {
    console_log_xml("uil_add_new_vertex called with bad input.");
    return 0;
  }

  var id = 0;
  if (ray_base && ray_direction) {
    // Check whether we are adding the vertex on an edge participating in symmetry.
    if (symmetry_edges_list_first.includes(edge_id) || symmetry_edges_list_second.includes(edge_id))
      id = add_vertex_on_symmetry_edge(edge_id, ray_base, ray_direction, -1);
    else
      id = add_vertex(edge_id, ray_base, ray_direction);
  }
  return id;
}

export function uil_add_vertex_at_param(edge_id, t) {
  // Check whether we are adding the vertex on an edge participating in symmetry.
  var id = 0;
  if (symmetry_edges_list_first.includes(edge_id) || symmetry_edges_list_second.includes(edge_id))
    id = add_vertex_on_symmetry_edge(edge_id, null, null, t);
  else
    id = add_vertex_at_param(edge_id, t);
  return id;
}

function add_vertex_on_symmetry_edge(edge_id, ray_base, ray_direction, t) {
  // Note: Either use (ray_base, ray_direction) when t = -1, or use t when (ray_base, ray_direction) are null

  // Find symmetric edge
  var sedge_id = 0;
  if (symmetry_edges_list_first.includes(edge_id))
    sedge_id = symmetry_edges_list_second[symmetry_edges_list_first.indexOf(edge_id)];
  else if (symmetry_edges_list_second.includes(edge_id))
    sedge_id = symmetry_edges_list_first[symmetry_edges_list_second.indexOf(edge_id)];

  // Add vertex on the first edge
  var new_vid = 0;
  if (t >= 0)
    new_vid = add_vertex_at_param(edge_id, t);
  else
    new_vid = add_vertex(edge_id, ray_base, ray_direction);

  if (sedge_id == 0 || new_vid == 0) {
    uid_unhighlight_all_objects();
    return new_vid;
  }

  // Find the coordinates of the point that is symmetric to new_vid.
  var mirror_p = find_mirror_point(uid_vertices.getItem(new_vid).object3d.position, symmetry_plane_base, symmetry_plane_normal);

  // Depending on whether we have a parameter t or a ray...
  var new_svid = 0;
  if (t >= 0) {
    // check whether we should use t or 1-t
    if (vector3.point_point_distance(mirror_p, get_point_at_edge_param(sedge_id, t)) >
        vector3.point_point_distance(mirror_p, get_point_at_edge_param(sedge_id, (1.0-t))))
      t = 1.0 - t;

    // Add point at t
    new_svid = add_vertex_at_param(sedge_id, t);
  }
  else {
    // Use mirror point and ray direction to add new point
    new_svid = add_vertex(sedge_id, mirror_p, ray_direction);
  }

  if (new_svid == 0) {
    uid_unhighlight_all_objects();
    return new_vid;
  }

  // Add new edges to symmetry set
  var new_edges = [uil_get_edges_of_vertex(new_vid)[0], uil_get_edges_of_vertex(new_vid)[1],
                   uil_get_edges_of_vertex(new_svid)[0], uil_get_edges_of_vertex(new_svid)[1]];

  add_to_symmetry_set(new_edges, []);
  uid_unhighlight_all_objects();

  return new_vid;

/*
    uid_raycaster.setFromCamera(mousenormalized, uid_camera);
    var wv_parameter = new _WrappedValue__WEBPACK_IMPORTED_MODULE_17__["default"]();
    var intersection_point = uil_get_ray_edge_intersection(uido.id, uid_raycaster.ray.origin, uid_raycaster.ray.direction, true, wv_parameter);
*/

}

export function uil_add_new_edge(face_id, vertex1_id, vertex2_id, sample_points, sample_normals, is_planar) {
  // Check that the two vertices are valid
  if (vertex1_id == 0 || vertex2_id == 0) {
    console_log_xml("ERROR: INVALID VERTICES ", vertex1_id, " ", vertex2_id);
    return 0;
  }

  if (sample_points.length > 0 ) {
    // substitute the 1st and last sample with the coordinates of the two vertices
    var v1 = uid_vertices.getItem(vertex1_id);
    var v2 = uid_vertices.getItem(vertex2_id);
    sample_points[0] = v1.object3d.position;
    sample_points[sample_points.length-1] = v2.object3d.position;
  }

  if (sample_points.length < 15) {
    // Not enough samples. Revert to adding "straight" edge between two vertices.
    // Note that:
    // If fitted curve freedom corresponds to n+1 control points
    // and number of samples = m+1
    // the condition is m > n + 5
    // Currently n = 9, so m must be greater than 14.

    sample_points = [];
    sample_normals = [];

    // Trey to recalculate the sample points
    var linedir = new THREE.Vector3();
    var lineok = uil_calc_ray_dir_for_face_sampling(face_id, vertex1_id, vertex2_id, linedir);
    if (lineok) {
      var res = calculate_sample_planar_points_for_add_edge(30,
              uid_vertices.getItem(vertex1_id).object3d.position, uid_vertices.getItem(vertex2_id).object3d.position,
              face_id, linedir, sample_points, sample_normals);
      if (!res) {
        var sample_points = [];
        var sample_normals = [];
      }
    }
  }
  else {
    var sample_coords = [];
    var sample_normal_coords = []
    //clear_small_balls(); //XXX
    sample_points.forEach(function(p){
      //add_small_ball(p.x, p.y, p.z, .5, .5, .5); // XXX
      sample_coords.push(p.x);
      sample_coords.push(p.y);
      sample_coords.push(p.z);
    });

    sample_normals.forEach(function(n){
      //add_small_ball(p.x, p.y, p.z, .5, .5, .5); // XXX
      sample_normal_coords.push(n.x);
      sample_normal_coords.push(n.y);
      sample_normal_coords.push(n.z);
    });
  }

  if (face_is_flat(face_id)) {
    var id = add_edge(face_id, vertex1_id, vertex2_id, sample_points, sample_normals, is_planar);
  }
  else {
    // Set up mechanism for inflating/deflating new faces after the edge is added to bring them as close to the
    // original face as possible

    suppress_animate();
    suppress_undo_tracking();

    // We'll get a bunch of samples on the surface
    var b = new THREE.Vector3();
    var d = new THREE.Vector3();
    var n = new THREE.Vector3();
    var wv_r = new WrappedValue(0);
    var wv_f = new WrappedValue(0);
    uil_get_face_geometry(face_id, b, n, wv_r, d, wv_f);
    uid_add_edge_face_normal_before = new THREE.Vector3();
    uid_add_edge_face_normal_before.copy(n);
    uid_add_edge_face_sample_points_before = [];
    var step = wv_r.v / 3.0;
    var d1 = new THREE.Vector3();
    d1.crossVectors(n, d);
    var i, j;
    var v = new THREE.Vector3();
    var wv_bogus = new WrappedValue();
    for (i = -2; i <= 2; i++) {
      for (j = -2; j <= 2; j++) {
        v.copy(b);
        v.addScaledVector(d, i*step);
        v.addScaledVector(d1, j*step);
        var p = uil_get_face_ray_intersection(face_id, v, n, wv_bogus, wv_bogus, true);
        //add_small_ball(v.x, v.y, v.z, 0, 255, 0);
        if (p) {
          uid_add_edge_face_sample_points_before.push(p);
          //add_small_ball(p.x, p.y, p.z, 200, 50, 50, 0.5);
        }
      }
    }

    // Perform the actuall edge addition
    var id = add_edge(face_id, vertex1_id, vertex2_id, sample_points, sample_normals, is_planar);

    if (id) {
      // Before returning the new face id, see if we can improve on its shape by inflating/deflating
      //log_reset_timer("A");
      // Only do this if we have at least 8 total points
      if (uid_add_edge_face_sample_points_before.length >= 6) {
        uil_start_compound_operation();
        suppress_ui_messages();
        if (uil_get_edges_of_face(uid_api_add_edge_new_face1_id).length > 4)
          adjust_face_inflating_to_target(uid_api_add_edge_new_face1_id, uid_add_edge_face_sample_points_before, uid_add_edge_face_normal_before, 3);
        if (uil_get_edges_of_face(uid_api_add_edge_new_face2_id).length > 4)
          adjust_face_inflating_to_target(uid_api_add_edge_new_face2_id, uid_add_edge_face_sample_points_before, uid_add_edge_face_normal_before, 3);
        unsuppress_ui_messages_if_ready();
        uil_end_compound_operation();
      }
      else {
        console_log("Skipping inflating face");
      }
    }
    else {
      console_log_xml("Failed to add edge on non-planar face");
    }
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
  }
  return id;
}

export function uil_add_new_edge_quickly(face_id, vertex1_id, vertex2_id) {
  if (vertex1_id == 0 || vertex2_id == 0) {
    console_log_xml("ERROR: INVALID VERTICES ", vertex1_id, " ", vertex2_id);
    return 0;
  }

    var id = add_edge(face_id, vertex1_id, vertex2_id, [], [], face_is_flat(face_id));
    return id;
}

export function uil_modify_edge_geometry(edge_id, vertex1_id, vertex2_id, samples) {
  var v1 = uid_vertices.getItem(vertex1_id);
  var v2 = uid_vertices.getItem(vertex2_id);
  var local_epsilon = 1e-4;

  var done = false;
  while (!done && samples.length > 2) {
    if (maths.proximate(vector3.point_point_distance(v1.object3d.position, samples[0]), 0, local_epsilon))
      samples.splice(0,1);
    else
      done = true;
  }
  done = false;
  while (!done && samples.length > 2) {
    if (maths.proximate(vector3.point_point_distance(v2.object3d.position, samples[samples.length-1]), 0, local_epsilon))
      samples.pop();
    else
      done = true;
  }

  if (samples.length < 20) {
    display_message_to_user(LOG.WARN, "Not enough samples");
  }

  // Break any tangencies on this edge
  var neids = get_neighbouring_edges_of_edge(edge_id);
  neids.forEach(function(neid){
    uil_remove_joint_smoothness([[edge_id, neid]], true, true);
  });

  // Remove any linearity flags from this edge
  var edge_was_linear = false;
  if (uil_is_edge_linear(edge_id)) {
    edge_was_linear = true;
    uil_set_linearity_of_edges([edge_id], false);
  }

  // See if we need to flip the samples order and vertices to go from 1st to 2nd edge vertex
  var edge_vertices = uil_get_vertices_of_edge(edge_id);
  var res = 0;
  if (edge_vertices[0] == vertex1_id && edge_vertices[1] == vertex2_id)
    // All is good, proceed
    res = modify_edge_geometry(edge_id, vertex1_id, vertex2_id, samples);
  else if (edge_vertices[0] == vertex2_id && edge_vertices[1] == vertex1_id)
    res = modify_edge_geometry(edge_id, vertex2_id, vertex1_id, samples.reverse());
  else
    console_log_xml("ERROR: modify_edge_geometry vertices unacceptable.");
  if (res && edge_was_linear && uil_is_edge_linear_geometry(edge_id))
    // Set back linearity flag if needed
    uil_set_linearity_of_edges([edge_id], true);

  return res;
}

export function uil_set_vertex_distance(v1id, v2id, dist) {
  return set_vertex_distance(v1id, v2id, dist);
}

export function uil_make_edges_linear_as_one(ids_of_edges_to_make_linear, force = false) {
  // First we must remove all internal tangencies
  var listified_edges = [];
  var wv_is_open_list = new WrappedValue();
  if (uil_listify_edges(ids_of_edges_to_make_linear, wv_is_open_list, listified_edges)) {
    for (var i = 0; i < listified_edges.length-1; i++) {
      uil_remove_joint_smoothness([[listified_edges[i], listified_edges[i+1]]], false, true);
    }
  }

  // We also remove any tangencies with external edges at the two ends of the edge list
  if (wv_is_open_list.v) {
    var first_edge_id = listified_edges[0];
    var second_edge_id = listified_edges[1];
    var one_before_last_edge_id = listified_edges[listified_edges.length-2];
    var last_edge_id = listified_edges[listified_edges.length-1];

    var vfs = uil_find_edge_shared_vertex(first_edge_id, second_edge_id);
    var vol = uil_find_edge_shared_vertex(one_before_last_edge_id, last_edge_id);

    var vs = uil_get_vertices_of_edge(first_edge_id);
    var vA = (vfs == vs[0] ? vs[1] : vs[0]);
    var vs = uil_get_vertices_of_edge(last_edge_id);
    var vB = (vol == vs[0] ? vs[1] : vs[0]);

    var esA = uil_get_edges_of_vertex(vA);
    var esB = uil_get_edges_of_vertex(vB);

    var tangent_at_beginning = 0;
    esA.forEach(function(eAid) {
      if (eAid != first_edge_id)
        uil_remove_joint_smoothness([[eAid, first_edge_id]], false, true);
    });

    var tangent_at_end = 0;
    esB.forEach(function(eBid) {
      if (eBid != last_edge_id)
        uil_remove_joint_smoothness([[eBid, last_edge_id]], false, true);
    });
  }

  var success_flag = make_edges_linear_as_one(ids_of_edges_to_make_linear);
  request_setting_button_states();
  return(success_flag);
}

export function uil_inflate_face(fid, nsteps = 3) {
  if (uil_is_object_fixed(fid)) {
    //display_message_to_user(LOG.WARN, "Face is fixed.");
  }
  else if (face_is_flat(fid)) {
    //display_message_to_user(LOG.WARN, "Cannot directly pull on a flat face; try its edges or vertices instead.");
  }
  else if (uil_can_inflate(fid) < nsteps) {
    //display_message_to_user(LOG.WARN, "You reached the stretch limit for this face.");
  }
  else
    return inflate_face(fid, nsteps);
  return 0;
}

export function uil_deflate_face(fid, nsteps = 3) {
  if (uil_is_object_fixed(fid)) {
    //display_message_to_user(LOG.WARN, "Face is fixed.");
  }
  else if (face_is_flat(fid)) {
    //display_message_to_user(LOG.WARN, "Cannot directly pull on a flat face; try its edges or vertices instead.");
  }
  else if (uil_can_deflate(fid) < nsteps) {
    //display_message_to_user(LOG.WARN, "You reached the stretch limit for this face.");
  }
  else
    return deflate_face(fid, nsteps);
  return 0;
}

export function uil_flatten_objects() {

  // First check that no selected edges or selected faces or edges around selected faces are fixed.
  // We will make an exception in case the selected vertices are the only fixed.
/*
  var vids = [];
  if (uid_selected_vertices.length == 3) {
    vids.push(uid_selected_vertices.getKeyAtIndex(0));
    vids.push(uid_selected_vertices.getKeyAtIndex(1));
    vids.push(uid_selected_vertices.getKeyAtIndex(2));
  }
*/
  var cannot_flatten_due_to_fixing = false;
  uid_selected_faces.keys().forEach(function(fid){
    if (uil_is_object_fixed(fid))
      cannot_flatten_due_to_fixing = true;
    var l = uil_get_edges_of_face(fid);
    l.forEach(function(eid) {
      if (uil_is_object_fixed(eid))
        cannot_flatten_due_to_fixing = true;
      var ll = uil_get_vertices_of_edge(eid);
      ll.forEach(function(vid){
        if (uil_is_object_fixed(vid) /* && vids.indexOf(vid) == -1 */)
          cannot_flatten_due_to_fixing = true;
      });
    });
  });

  if (!cannot_flatten_due_to_fixing) {
    uid_selected_edges.keys().forEach(function(eid){
      if (uil_is_object_fixed(eid))
        cannot_flatten_due_to_fixing = true;
      var l = uil_get_vertices_of_edge(eid);
      l.forEach(function(vid){
        if (uil_is_object_fixed(vid) /* && vids.indexOf(vid) == -1 */)
          cannot_flatten_due_to_fixing = true;
      });
    });
  }

  if (cannot_flatten_due_to_fixing) {
    display_message_to_user(LOG.WARN, "Cannot flatten due to fixed objects.")
    uid_deselect_all_objects();
    return;
  }

  // Plane base and normal
  var b;
  var n;

  var n_flat_faces = 0;
  var first_flat_face_id = null;
  var faces_to_flatten = [];

  // Check if we have selected vertices or not
  if (uid_selected_vertices.length == 3) {
    var v1 = uid_selected_vertices.getItemAtIndex(0);
    var v2 = uid_selected_vertices.getItemAtIndex(1);
    var v3 = uid_selected_vertices.getItemAtIndex(2);
    b = v1.object3d.position;
    n = triangle_normal(
      v1.object3d.position,
      v2.object3d.position,
      v3.object3d.position
    );
  }
  else {
    // Check whether we have a flat face
    for (var i = 0; i <= uid_selected_faces.length - 1; i++) {
      var f = uid_selected_faces.getKeyAtIndex(i);
      faces_to_flatten.push(f);
      if (face_is_flat(f)) {
        n_flat_faces++;
        if (n_flat_faces == 1)
          first_flat_face_id = f;
      }
    }

    if (n_flat_faces > 0) {
      // At least one flat face was found, use the first one to get a plane.
      b = new THREE.Vector3();
      var d = new THREE.Vector3();
      n = new THREE.Vector3();
      var wv_r = new WrappedValue(0);
      var wv_f = new WrappedValue(0);
      uil_get_face_geometry(first_flat_face_id, b, n, wv_r, d, wv_f);
    }
    else if (n_flat_faces == 0) {
      // No flat faces were found, so we have to use all edges to get a plane.
      // Create a list of all edges to flatten, either from directly selected edges, or from selected faces
      var edges_to_consider = [];
      uid_selected_edges.keys().forEach(function(e) {
        edges_to_consider.push(e);
      });
      uid_selected_faces.keys().forEach(function(f) {
        var l = uil_get_edges_of_face(f);
        l.forEach(function(e) {
          edges_to_consider.push(e);
        });
      });

      // Remove duplicate edges.
      var unique = [];
      $.each(edges_to_consider, function(i, el) {
        if ($.inArray(el, unique) === -1) unique.push(el);
      });
      edges_to_consider = unique.slice(0);

      b = new THREE.Vector3();
      var d = new THREE.Vector3();
      var wv_r = new WrappedValue(0);
      n = new THREE.Vector3();
      uil_get_edge_list_geometry(edges_to_consider, false, b, n, wv_r, d);

      // Check if normal n is within 10 degrees of one of the X,Y,Z axes, and if so adapt to it.
      var vx = new THREE.Vector3(1.0, 0.0, 0.0);
      var vy = new THREE.Vector3(0.0, 1.0, 0.0);
      var vz = new THREE.Vector3(0.0, 0.0, 1.0);

      var r1 = 0.349066; // 20 degrees
      var r2 = 2.79253; // 160 degrees
      if (Math.abs(vector3.angle(n, vx)) < r1 || Math.abs(vector3.angle(n, vx)) > r2)
        n.copy(vx);
      else if (
        Math.abs(vector3.angle(n, vy)) < r1 ||
        Math.abs(vector3.angle(n, vy)) > r2
      )
        n.copy(vy);
      else if (
        Math.abs(vector3.angle(n, vz)) < r1 ||
        Math.abs(vector3.angle(n, vz)) > r2
      )
        n.copy(vz);
    }
  }

  // Create list of edges to flatten
  var edges_to_flatten = [];
  uid_selected_edges.keys().forEach(function(e) {
    edges_to_flatten.push(e);
  });
  uid_selected_faces.keys().forEach(function(f) {
    if (n_flat_faces != 1 || (n_flat_faces == 1 && first_flat_face_id != f)) {
      var l = uil_get_edges_of_face(f);
      l.forEach(function(e) {
        edges_to_flatten.push(e);
      });
    }
  });

  // Remove duplicate edges.
  var unique = [];
  $.each(edges_to_flatten, function(i, el) {
    if ($.inArray(el, unique) === -1) unique.push(el);
  });
  edges_to_flatten = unique.slice(0);

  // Finally check that there are no tangencies between the edges in this set and any edges outside the set, in which case we remove them
  // to avoid it complaining.
  suppress_undo_tracking();
  suppress_animate();
  edges_to_flatten.forEach(function(eid){
    var neids = get_neighbouring_edges_of_edge(eid);
    neids.forEach(function(neid){
      if (edges_to_flatten.indexOf(neid) == -1) {
        uil_remove_joint_smoothness([[eid, neid]], false, true);
      }
    });
  });

  // Call core
  uid_deselect_all_objects();
  var res = make_edges_planar(edges_to_flatten, b, n);

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);

  // Check whether they are indeed planar
  faces_to_flatten.forEach(function(fid){
    if (!face_is_flat(fid))
      display_message_to_user(LOG.WARN, "At least one flattened face contains a fold and is not perfectly planar.");
  });
}

export function uil_get_edge_freedom(eid) {
  return get_edge_freedom(eid);
}

export function uil_get_edge_length(eid) {
  return get_edge_length(eid);
}

export function uil_set_edge_freedoms(edge_ids, freedom) {
  return set_edge_freedoms(edge_ids, freedom);
}


export function uil_pop_face(fid, is_in, dir_vec = null, dist = 0) {

  // If dist is 0, we calculate a default pop-out distance
  if (dist == 0)
    dist = Number((uid_calculate_bounding_box_of_objects([], [], [fid], true).dimension/5.0).toPrecision(2));

  if (uil_is_object_fixed(fid)) {
    display_message_to_user(LOG.WARN, "Cannot pop a fixed face.");
    return [0, null];
  }

  /* No longer adjusting ring of faces as they are quads
  suppress_undo_tracking();
  var old_edge_ring = uil_get_edges_of_face(fid);
  */

  var pop_vector = new THREE.Vector3();
  var new_face_id = pop_face(fid, is_in, dir_vec, dist, pop_vector);
  if (!new_face_id)
    return [0, null];

  /* No longer adjusting ring of edges as they are quads
  uil_start_compound_operation();
  suppress_ui_messages();

  var new_edge_ring = uil_get_edges_of_face(new_face_id);

  var faces_to_adjust = [];
  var target_points_on_faces_to_adjust = [];
  var normals_of_faces_to_adjust = [];

  new_edge_ring.forEach(function(eid){
    var fs = uil_get_faces_of_edge(eid);
    var perpendicular_face = fs[0] = new_face_id ? fs[1] : fs[0];
    var pes = uil_get_edges_of_face(perpendicular_face);
    var opposite_edge;
    pes.forEach(function(peid){
      if (old_edge_ring.indexOf(peid) != -1)
        opposite_edge = peid;
    });
    var p1 = uil_get_point_at_edge_param(eid, 0.5);
    var p2 = uil_get_point_at_edge_param(opposite_edge, 0.5);
    var p = new THREE.Vector3();
    p.addVectors(p1, p2).multiplyScalar(0.5);
    faces_to_adjust.push(perpendicular_face);
    target_points_on_faces_to_adjust.push(p);

    var b = new THREE.Vector3();
    var d = new THREE.Vector3();
    var n = new THREE.Vector3();
    var wv_r = new WrappedValue(0);
    var wv_f = new WrappedValue(0);
    uil_get_face_geometry(perpendicular_face, b, n, wv_r, d, wv_f);
    normals_of_faces_to_adjust.push(n);
  });

  for (var i=0; i < faces_to_adjust.length; i++) {
    var fid = faces_to_adjust[i];
    if (!face_is_flat(fid)) {
      var p = target_points_on_faces_to_adjust[i];
      var n = normals_of_faces_to_adjust[i];
      adjust_face_inflating_to_target(fid, [p], n, 1);
    }
  }

  unsuppress_ui_messages_if_ready();
  uil_end_compound_operation();
  unsuppress_undo_tracking_if_ready(false);
  */

  return [new_face_id, pop_vector];
}

export function uil_pop_faces(face_ids, is_in, dir_vec = null, dist = 0) {

  // Note: In this case, the IDs of the original faces being popped (face_ids) is the same as for the new popped faces "replacing them", as we're modifying the
  // existing faces.

  // If dist is 0, we calculate a default pop-out distance
  if (dist == 0) {
    const n = face_ids.length;
    face_ids.forEach(function(fid){
      dist += Number((uid_calculate_bounding_box_of_objects([], [], [fid], true).dimension/5.0).toPrecision(2));
    });
    dist /= n;
  }

  // If any fixed objects, stop.
  face_ids.forEach(function(fid){
    if (uil_is_object_fixed(fid)) {
      display_message_to_user(LOG.WARN, "Cannot pop a fixed face.");
      return [0, null];
    }
  });

  /*
  old_edge_ring = get_list_of_edges_around_faces(face_ids);

  suppress_undo_tracking();
  */

  var pop_vector = new THREE.Vector3();
  var res = pop_faces(face_ids, is_in, dir_vec, dist, pop_vector);

  if (!res)
    return 0;

  /*
  uil_start_compound_operation();
  suppress_ui_messages();

  // Create list of boundary edges around popped faces
  new_edge_ring = get_list_of_edges_around_faces(face_ids);

  // Perform inflate/deflate to adjust surrounding faces
  var faces_to_adjust = [];
  var target_points_on_faces_to_adjust = [];
  var normals_of_faces_to_adjust = [];

  new_edge_ring.forEach(function(eid){
    var fs = uil_get_faces_of_edge(eid);
    var perpendicular_face = (face_ids.indexOf(fs[0]) == -1) ? fs[0] : fs[1];
    var pes = uil_get_edges_of_face(perpendicular_face);
    var opposite_edge;
    pes.forEach(function(peid){
      if (old_edge_ring.indexOf(peid) != -1)
        opposite_edge = peid;
    });
    var p1 = uil_get_point_at_edge_param(eid, 0.5);
    var p2 = uil_get_point_at_edge_param(opposite_edge, 0.5);
    var p = new THREE.Vector3();
    p.addVectors(p1, p2).multiplyScalar(0.5);
    faces_to_adjust.push(perpendicular_face);
    target_points_on_faces_to_adjust.push(p);

    var b = new THREE.Vector3();
    var d = new THREE.Vector3();
    var n = new THREE.Vector3();
    var wv_r = new WrappedValue(0);
    var wv_f = new WrappedValue(0);
    uil_get_face_geometry(perpendicular_face, b, n, wv_r, d, wv_f);
    normals_of_faces_to_adjust.push(n);
  });

  for (var i=0; i < faces_to_adjust.length; i++) {
    var fid = faces_to_adjust[i];
    if (!face_is_flat(fid)) {
      var p = target_points_on_faces_to_adjust[i];
      var n = normals_of_faces_to_adjust[i];
      adjust_face_inflating_to_target(fid, [p], n, 1);
    }
  }

  unsuppress_ui_messages_if_ready();
  uil_end_compound_operation();
  unsuppress_undo_tracking_if_ready(false);
  */

  return pop_vector;
}

/* Obsolete previous version that does not do the inflate/deflate,
   keeping it just in case something goes wrong.
function uil_pop_faces_without_inflate_deflate(face_ids, is_in) {

  face_ids.forEach(function(fid){
    if (uil_is_object_fixed(fid)) {
      display_message_to_user(LOG.WARN, "Cannot pop a fixed face.");
      return [0, null];
    }
  });

  var pop_vector = new THREE.Vector3();
  var res = pop_faces(face_ids, is_in, pop_vector);
  if (!res)
    return 0;
  return pop_vector;
}
*/


export function uil_make_hole_or_handle_multi(f1ids, f2ids, is_hole, v1id, v2id, stop_if_zero_distance = false, modifier = false) {

    var fixed_found = false;
    f1ids.forEach(function(fid){
        if (uil_is_object_fixed(fid))
            fixed_found = true;
    });
    f2ids.forEach(function(fid){
        if (uil_is_object_fixed(fid))
            fixed_found = true;
    });

    if (fixed_found) {
        display_message_to_user(LOG.WARN, "Cannot make hole bridge on fixed face.");
        return;
    }

    suppress_animate();
    suppress_undo_tracking();

    // Check whether we have any 0-distances between vertices of the two face boundaries

    // Find all edges on the boundary of the two sets of faces
    var boundary_vertex_ids1 = new Array();
    var boundary_edge_ids1 = new Array();
    var wv_is_one_cc1 = new WrappedValue();
    var boundary_vertex_ids2 = new Array();
    var boundary_edge_ids2 = new Array();
    var wv_is_one_cc2 = new WrappedValue();
    var res = uil_get_face_connected_component_info(f1ids, wv_is_one_cc1, boundary_edge_ids1, boundary_vertex_ids1);
    var res = uil_get_face_connected_component_info(f2ids, wv_is_one_cc2, boundary_edge_ids2, boundary_vertex_ids2);

    var eps = 1e-05;
    var zero_dist_found = false;
    var zero_dist_pairs = [];
    boundary_vertex_ids1.forEach(function(v1){
        boundary_vertex_ids2.forEach(function(v2){
            if (maths.proximate(vector3.point_point_distance(uid_vertices.getItem(v1).object3d.position, uid_vertices.getItem(v2).object3d.position), 0, eps)) {
                zero_dist_found = true;
                zero_dist_pairs.push([v1, v2]);
            }
        });
    });

    if (zero_dist_found && !stop_if_zero_distance) {

        var zero_dist_edges_to_remove = [];
        var zero_dist_initial_edge_description = new HashTable();

        // Copy list of edges of 2nd boundary
        zero_dist_edges_to_remove = [...boundary_edge_ids2];

        // From the list of edges to remove, remove any that don't have both vertices in zero_dist_pairs (2nd entry)
        for (var i = zero_dist_edges_to_remove.length - 1 ; i >= 0 ; i -- ) {
            var eid = zero_dist_edges_to_remove[i];
            var vs = uil_get_vertices_of_edge(eid);
            var v1_in_pair = false;
            var v2_in_pair = false;
            zero_dist_pairs.forEach(function(pair){
                if (pair[1] == vs[0])
                    v1_in_pair = true;
                if (pair[1] == vs[1])
                    v2_in_pair = true;
            });
            if (!v1_in_pair || !v2_in_pair)
                zero_dist_edges_to_remove.splice(i,1);
        }

        zero_dist_pairs.forEach(function(pair){

            // Find the edgeσ attached to the second vertex that are not part of the face boundary
            var es1 = uil_get_edges_of_vertex(pair[1]);
            var nfound = 0;
            es1.forEach(function(eid){
                // We want edges with 0 faces in the f2ids group
                var efs = uil_get_faces_of_edge(eid);
                var evs = uil_get_vertices_of_edge(eid);
                if ((f2ids.indexOf(efs[0]) == -1 && f2ids.indexOf(efs[1]) == -1) ||
                    (boundary_edge_ids2.indexOf(eid) != -1 &&  zero_dist_edges_to_remove.indexOf(eid) == -1)) {
                    var evs = uil_get_vertices_of_edge(eid);
                    var edge_samples = [];
                    uid_edges.getItem(eid).object3d.geometry.vertices.forEach(function(v3d){
                        edge_samples.push(new THREE.Vector3(v3d.x, v3d.y, v3d.z));
                    });
                    var edge_description = {};
                    edge_description["id"] = eid;
                    edge_description["v2"] = (evs[0] == pair[1] ? evs[1] : evs[0]);
                    edge_description["samples"] = (evs[0] == pair[1] ? edge_samples : edge_samples.reverse());
                    if (zero_dist_initial_edge_description.hasItem(pair[1])) {
                        var darr = zero_dist_initial_edge_description.getItem(pair[1]);
                        darr.push(edge_description)
                        zero_dist_initial_edge_description.setItem(pair[1], darr);
                    }
                    else
                        zero_dist_initial_edge_description.setItem(pair[1], [edge_description]);
                    nfound++;
                }
            });

            // Move the 2nd vertex of the pair along the normal of the face being bridged
            // Find the corresponing face
            var vertex_fid;
            var v2id = pair[1];
            f2ids.forEach(function(fid){
                var vs = vertices_of_face(fid);
                if (vs.indexOf(v2id) != -1)
                    vertex_fid = fid;
            });
            // Find normal of this face
            var center = new THREE.Vector3();
            var wv_radius = new WrappedValue(0);
            var wv_flatness = new WrappedValue(0);
            var characteristic_dir_coords = new THREE.Vector3();
            var normal = new THREE.Vector3();
            uil_get_face_geometry(vertex_fid, center, normal, wv_radius, characteristic_dir_coords, wv_flatness);
            // Move vertex by a small amount
            var eps = 5e-3;
            var p = new THREE.Vector3();
            p.copy(uid_vertices.getItem(v2id).object3d.position);
            p.addScaledVector(normal.normalize(), eps * -1);
            uil_move_vertex_to_coords( v2id, p.x, p.y, p.z );
        });
    }
    else if (zero_dist_found) {
        display_message_to_user(LOG.WARN, "Currently cannot bridge 0-distance edges.");
        unsuppress_undo_tracking_if_ready(false);
        unsuppress_animate_if_ready();
        request_setting_button_states();
        return 0;
    }

    // Keep a list of all existing edges before the operation
    var edge_list_before = [... uid_edges.keys()];
    var res = make_hole_or_handle_multi(f1ids, f2ids, is_hole, v1id, v2id);

    if (res && !modifier) {
        // Make all new edges linear
        var new_edge_list_after = [];
        uid_edges.keys().forEach(function(eid){
            if (edge_list_before.indexOf(eid) == -1) {
                new_edge_list_after.push(eid);
            }
        });
        uil_set_linearity_of_edges(new_edge_list_after, true);
    }

    // We now need to handle the 0-distance cases.
    if (zero_dist_found) {

        // Delete edges that form old face boundary.
        uid_deselect_all_objects(0);
        var edges_to_delete = [];
        zero_dist_edges_to_remove.forEach(function(eid){
            push_nodups(eid, edges_to_delete);
        });
        uil_delete_edges(edges_to_delete, false);

        uid_deselect_all_objects();

        // Delete boundary vertices, one by one, and set original edge shape.
        var error_encountered = false;
        zero_dist_pairs.forEach(function(pair) {
            if (error_encountered)
              return;

            // Delete the vertex
            uil_delete_vertices([pair[1]], false);

            // Now set the geometry of the resulting edge after deleting the vertex to the initial edge geometry.
            // One or more edges could have been attached to this vertex.
            if (!pair) {
              error_encountered = true;
              return;
            }
            var edge_descriptions_array = zero_dist_initial_edge_description.getItem(pair[1]);
            if (!edge_descriptions_array) {
              error_encountered = true;
              return;
            }
            if (edge_descriptions_array.length == 1) {
                // It was just one edge. So deleting the vertex has left us with one new edge.
                // We will deform this edge to give it the initial sampled shape.
                var edge_description = edge_descriptions_array[0];
                var v1 = pair[0];
                var v2 = edge_description.v2;
                var eid = find_edge_from_two_vertices(v1, v2);
                uil_modify_edge_geometry(eid, v1, v2, edge_description.samples);
            }
            else {
                // It was more than one edge. Deleting the vertex will remove all edges, so we have to add them back one by one, and
                // after each addition we have to also set is shape as initially sampled
                edge_descriptions_array.forEach(function(edge_description){
                    var v1 = pair[0];
                    var v2 = edge_description.v2;
                    var fid = find_face_from_two_vertices(v1, v2);
                    if (fid != 0) {

                      // Make sure there's no edge there already
                      let edge_exists = false;
                      uid_edges.keys().forEach(function(eid){
                          let vs = uil_get_vertices_of_edge(eid);
                          if ((vs[0] == v1 && vs[1] == v2) || (vs[1] == v1 && vs[0] == v2)) {
                              edge_exists = true;
                          }
                      });
                      if (!edge_exists) {
                        var eid = add_edge_between_two_vertices_on_face(v1, v2, fid, null, true);
                        if (eid)
                          uil_modify_edge_geometry(eid, v1, v2, edge_description.samples);
                      }
                    }
                });
            }
        });
    }

    if (error_encountered) {
      display_message_to_user(LOG.WARN, "Looks like something went wrong while attempting to bridge between coincident vertices. Please hit undo.");
    }

    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    request_setting_button_states();

    return res;
}

export function uil_is_edge_linear(eid) {
  return is_edge_linear(eid);
}

export function uil_is_edge_linear_geometry(eid) {
  return is_edge_linear_geometry(eid);
}

export function uil_set_linearity_of_edges(ids_of_edges, make_linear, force = false) {

  var cannot_proceed_due_to_fixed_objects = false;
  var unfixed_edge_tangents = 0;
  ids_of_edges.forEach(function(eid){
    if (uil_is_object_fixed(eid))
      cannot_proceed_due_to_fixed_objects = true;
  });

  // Check whether any edge is non-linear and has fixed tangents
  if (!cannot_proceed_due_to_fixed_objects) {
    var wv = new WrappedValue();

    ids_of_edges.forEach(function(eid){
      if (!uil_is_edge_linear(eid)) {
        uil_get_edge_fixing_of_tangent_at_vertex(eid, uil_get_vertices_of_edge(eid)[0], wv);
        if (wv.v) {
          uil_set_edge_fixing_of_tangent_at_vertex(eid, uil_get_vertices_of_edge(eid)[0], false);
          unfixed_edge_tangents++;
        }
        uil_get_edge_fixing_of_tangent_at_vertex(eid, uil_get_vertices_of_edge(eid)[1], wv);
        if (wv.v) {
          uil_set_edge_fixing_of_tangent_at_vertex(eid, uil_get_vertices_of_edge(eid)[1], false);
          unfixed_edge_tangents++;
        }
      }
    });
  }

  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot make linear fixed edges or edges with fixed tangents.");
    return;
  }

  if (unfixed_edge_tangents == 1)
    display_message_to_user(LOG.WARN, "One of the two edge tangents were unfixed to allow the edge to become linear.");
  else if (unfixed_edge_tangents == 2)
    display_message_to_user(LOG.WARN, "The two edge tangents were unfixed to allow the edge to become linear.");

  // If we're going to make edges linear, before starting the process we have to remove any
  // tangencies that are on them, unless they are already geometrically linear.
  // We must also let the user know about this.

  suppress_undo_tracking();
  suppress_animate();

  // If force flag is set we don't need to remove the tangencies because the core will do this.
  if (!force) {
    suppress_ui_messages(); // So we don't repeatedly warn the user about removing tangency
    if (make_linear) {
      ids_of_edges.forEach(function(eid){
        // Check if the edge is tangent with any other edge, and if so remove tangency.
        var adjacent_edges = [];
        var edge_vertices = uil_get_vertices_of_edge(eid);
        var e_array1 = uil_get_edges_of_vertex(edge_vertices[0]);
        var e_array2 = uil_get_edges_of_vertex(edge_vertices[1]);
        adjacent_edges = e_array1.concat(e_array2);
        adjacent_edges.forEach(function(aeid){
          if (uil_find_edge_shared_vertex(eid, aeid) != 0)
            uil_remove_joint_smoothness([[eid, aeid]], false, true);
        });
      });
    }
    unsuppress_ui_messages_if_ready();
  }

  var res = set_linearity_of_edges(ids_of_edges, make_linear, force);

  if (res) {
    // Set linearity flags of all edges.
    ids_of_edges.forEach(function(eid){
      var e = uid_edges.getItem(eid);
      e.set_linear_flag(make_linear);
    });
  }

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);

  // Highlight and unhiglight edges to set their appearance based on their new linearity state
  ids_of_edges.forEach(function(eid){
    set_default_edge_color(eid);
  });
  animate();

  return res;
}

export function uil_make_edges_circular(edge_ids, vertex_ids, center_vertex_id) {

  console_log("Make circular edges: ", edge_ids);

  suppress_undo_tracking();
  suppress_animate();
  // If we're going to make edges circular, before starting the process we have to remove any
  // tangencies that are on them. We must also let the user know about this.
  edge_ids.forEach(function(eid){
    // Check if the edge is tangent with any other edge, and if so remove tangency.
    var adjacent_edges = [];
    var edge_vertices = uil_get_vertices_of_edge(eid);
    var e_array1 = uil_get_edges_of_vertex(edge_vertices[0]);
    var e_array2 = uil_get_edges_of_vertex(edge_vertices[1]);
    adjacent_edges = e_array1.concat(e_array2);
    adjacent_edges.forEach(function(aeid){
      if (uil_find_edge_shared_vertex(eid, aeid) != 0)
        uil_remove_joint_smoothness([[eid, aeid]], false, true);
    });
  });

  var res =  make_edges_circular(edge_ids, vertex_ids, center_vertex_id);
  if (!res) {
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    set_button_states();
    return res;
  }

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  set_button_states();
  return res;
}

export function uil_can_make_edges_symmetric(e1, e2, b, n) {
  if (e1 == 0 || e2 == 0 || e1 == e2)
    return false;
  return can_make_edges_symmetric(e1, e2, b, n) == 1 ? true : false;
}

export function uil_make_edges_symmetric(e1, e2, b, n, dont_affect_modified_objects = false) {
  if (e1 == 0 || e2 == 0 || e1 == e2)
    return 0;
  var res = make_edges_symmetric(e1, e2, b, n, dont_affect_modified_objects);
  if (!res) {
    display_message_to_user(LOG.WARN, "Failed to apply symmetry to some edges. Possible reason is if the edges or any of their vertices are fixed.");
  }
  return res;
}

export function uil_is_vertex_of_edge(vertex_id, edge_id) {
  return is_vertex_of_edge(vertex_id, edge_id);
}

export function uil_can_make_edges_circular(edge_ids, vertex_ids, center_vertex_id) {
  return can_make_edges_circular(edge_ids, vertex_ids, center_vertex_id);
}

export function uil_can_make_face_circular(face_id) {
  return can_make_face_circular(face_id);
}

export function uil_make_face_circular(face_id) {
  return make_face_circular(face_id);
}

// function uil_make_face_planar(face_id) {
//   return make_face_planar(face_id);
// }

export function uil_triangulate_faces(face_ids, triangles_per_face) {
  return triangulate_faces(face_ids, triangles_per_face);
}

export function uil_set_display_face_triangulation_res(triangles_per_face) {
  return set_display_face_triangulation_res(triangles_per_face);
}

export function uil_get_display_face_triangulation_res() {
  return get_display_face_triangulation_res();
}

export function uil_set_display_face_triangulation_res_for_drag(triangles_per_face) {
  var n = set_display_face_triangulation_res_for_drag(triangles_per_face);
  if (n > 0)
    return n;
  console_log_xml("Error in set_display_face_triangulation_res_for_drag");
  return 100;
}

export function uil_get_display_face_triangulation_res_for_drag() {
  return get_display_face_triangulation_res_for_drag();
}

export function uil_set_face_tessel(on) {
  return set_face_tessel(on);
}

export function uil_set_body_label(body_ids, label_string) {
  var v = set_body_label(body_ids, label_string );
  return v;
}

export function uil_get_body_label(body_id) {
  return get_body_label(body_id);
}

export function uil_set_face_label(face_ids, label_string) {
  var v = set_face_label(face_ids, label_string );
  return v;
}

export function uil_get_face_label(face_id) {
  return get_face_label(face_id);
}

export function uil_set_face_material(face_ids, material_string) {
  var v = set_face_material(face_ids, material_string );
  //console_log(uil_get_face_material(face_ids[0]));
  return v;
}

export function uil_get_face_material(face_id) {
  var mat = get_face_material(face_id);

  if (!mat)
    mat = uid_face_materials.getItem(face_id);
  if (!mat)
    mat = uid_callback_face_material;
  if (!mat) {
     mat = "plastic";
     //uil_set_face_material([face_id], mat);
  }

  return mat;
}

export function uil_set_face_color(face_ids, colorhex) {
  var rgb = hexToRgb(colorhex);
  if (!rgb || isNaN(rgb.r) || isNaN(rgb.g) || isNaN(rgb.b))
    return null;

  var v = set_face_color(face_ids, hexToRgb(colorhex).r, hexToRgb(colorhex).g, hexToRgb(colorhex).b );
  //console_log("Color is: ", uil_get_face_color(face_ids[0]));
  return v;
}

export function uil_get_face_color(face_id) {
  var c = get_face_color(face_id);
  if (c)
    return c;
  else {
    // Use default color
    // return (rgbToHex(color_r_face, color_g_face, color_b_face));
    // NO! Use callback color if possible, otherwise default
    return (uid_callback_face_color ? uid_callback_face_color : rgbToHex(color_r_face, color_g_face, color_b_face));
  }
}

export function uil_undo() {
  // Make sure core face tessellation is not suppressed
  if (core_face_tessellation_is_suppressed())
    reset_core_face_tessellation();

  if (uid_raynode == uid_manip_raynode)
    uid_hide_rigid_move_manipulator(false, 0, false);

  undo();
  force_refresh();
}

export function uil_redo() {
  // Make sure core face tessellation is not suppressed
  if (core_face_tessellation_is_suppressed())
    reset_core_face_tessellation();

  if (uid_raynode == uid_manip_raynode)
    uid_hide_rigid_move_manipulator(false, 0, false);

  redo();
  force_refresh();
}

export function uil_can_undo() {
  return can_undo();
}

export function uil_can_redo() {
  return can_redo();
}

export function uil_set_save_model_compression_level(clevel) {
  return set_save_model_compression_level(clevel);
}

export function uil_can_inflate(fid) {
  if (face_is_flat(fid))
    return 0;
  else
    return (can_inflate(fid));
}

export function uil_can_deflate(fid) {
  if (face_is_flat(fid))
    return 0;
  else
    return can_deflate(fid);
}

export function uil_find_edge_shared_vertex(e1id, e2id) {
  return find_edge_shared_vertex(e1id, e2id);
}

export function uil_get_vertex_normal(vid) {
  var vec = get_vertex_normal(vid);
  return vec;
}

export function uil_get_edge_end_tangents(eid) {
  var vs = uil_get_vertices_of_edge(eid);
  var vecs = [];
  vecs.push(uil_get_edge_tangent_at_vertex(eid, vs[0]));
  vecs.push(uil_get_edge_tangent_at_vertex(eid, vs[1]));
  return vecs;
}

export function uil_get_edge_curvatures(eid, n_samples) {
  var returned_tuple = get_edge_curvatures(eid, n_samples);
    if (returned_tuple) {
    var l = uil_get_edge_length(eid);
    returned_tuple[1].forEach(function(v){
      v.multiplyScalar(-0.1);
    });
  }
  return returned_tuple;
}

export function uil_change_object_visibility(objects, make_visible, fix_tangencies = false) {
  change_object_visibility(objects, make_visible);
  // Make sure we're not left with pairs of tangent edges that are visible/invisible
  if (fix_tangencies)
    remove_tangencies_from_visible_invisible_edge_pairs();
}

export function uil_merge_bodies_on_edge(b1id, b2id, e1id, e2id, v1id, v2id) {
  suppress_undo_tracking();
  var res = merge_bodies_on_edge(b1id, b2id, e1id, e2id, v1id, v2id);
  if (res) {
    // Check whether either of the two edges need to be made invisible
    var fs = uil_get_faces_of_edge(e1id);
    if (!uil_is_object_visible(fs[0]) && !uil_is_object_visible(fs[1])) {
      var res = uil_change_object_visibility([e1id], false, false);
      uid_objects_raynode.remove(uid_edges.getItem(e1id).object3d);
      uid_visible_edge_mesh_raynode.remove(uid_edges.getItem(e1id).get_visible_edge_mesh());
    }
    var fs = uil_get_faces_of_edge(e2id);
    if (!uil_is_object_visible(fs[0]) && !uil_is_object_visible(fs[1])) {
      var res = uil_change_object_visibility([e2id], false, false);
      uid_objects_raynode.remove(uid_edges.getItem(e2id).object3d);
      uid_visible_edge_mesh_raynode.remove(uid_edges.getItem(e2id).get_visible_edge_mesh());
    }
  }
  unsuppress_undo_tracking_if_ready(false);
  return res;
}

export function uil_flip_body_inside_out(bid) {
  return flip_body_inside_out(bid);
}

export function uil_set_auto_add_tangencies(on) {
  return set_auto_add_tangencies(on);
}

export function uil_get_face_triangulation_info(face_id, wv_ncoords, wv_ntriangles) {
  return get_face_triangulation_info(face_id, wv_ncoords, wv_ntriangles);
}


export function uil_get_face_triangulation(face_id, ncoords, ntriangles, coords, triangles) {
  return get_face_triangulation(face_id, ncoords, ntriangles, coords, triangles);
}

export function uil_get_edge_planes_from_point_intersection(edge_id, vertex_coords, is_high_res, wv_tx, wv_ty, wv_tz) {
  return get_edge_planes_from_point_intersection(edge_id, vertex_coords, is_high_res, wv_tx, wv_ty, wv_tz);
}

export function uil_set_edge_tangent_at_vertex(edge_id, vertex_id, t) {
  if (uil_get_vertices_of_edge(edge_id).indexOf(vertex_id) == -1) {
    display_message_to_user(LOG.WARN, "Vertex must lie on edge");
    return;
  }
  // Remove edge linearity if needed
  suppress_undo_tracking();
  if (uil_is_edge_linear(edge_id))
    uil_set_linearity_of_edges([edge_id], false, true);
  var res = set_edge_tangent_at_vertex(edge_id, vertex_id, t);
  unsuppress_undo_tracking_if_ready(false);
  return res;
}

export function uil_get_edge_tangent_at_vertex(edge_id, vertex_id) {
  if (uil_get_vertices_of_edge(edge_id).indexOf(vertex_id) == -1) {
    display_message_to_user(LOG.WARN, "Vertex must lie on edge");
    return null;
  }
  var t = new THREE.Vector3();
  var res = get_edge_tangent_at_vertex(edge_id, vertex_id, t);
  if (res)
    return t;
  else
    return null;
}

export function uil_set_edge_fixing_of_tangent_at_vertex(edge_id, vertex_id, must_fix) {
  if (uil_get_vertices_of_edge(edge_id).indexOf(vertex_id) == -1) {
    display_message_to_user(LOG.WARN, "Vertex must lie on edge")
    return;
  }
  return set_edge_fixing_of_tangent_at_vertex(edge_id, vertex_id, must_fix);
}

export function uil_get_edge_fixing_of_tangent_at_vertex(edge_id, vertex_id, wv_is_fixed) {
  if (uil_get_vertices_of_edge(edge_id).indexOf(vertex_id) == -1) {
    display_message_to_user(LOG.WARN, "Vertex must lie on edge")
    return;
  }
  return get_edge_fixing_of_tangent_at_vertex(edge_id, vertex_id, wv_is_fixed);
}

/* OBSOLETE
function uil_get_edge_tangent_at_vertex(eid, vid) {
  var vecs = get_edge_end_tangents(eid);
  var vertices = uil_get_vertices_of_edge(eid);
  if (vertices[0] == vid)
    return vecs[0];
  else if (vertices[1] == vid)
    return vecs[1];
  else {
    console_log_xml("Failed in uil_get_edge_tangent_at_vertex for edge " + eid + " and vertex " + vid);
    return null;
  }
}
*/

export function uil_get_point_at_edge_param(eid, u) {
  var vec = get_point_at_edge_param(eid, u);
  return vec;
}

export function uil_get_tangent_at_edge_param(eid, u) {
  var vec = get_tangent_at_edge_param(eid, u);
  return vec;
}

export function uil_is_vertex_smooth(vid) {
  var res = is_vertex_smooth(vid);
  return res;
}

export function uil_is_joint_smooth(e1id, e2id) {
  if (!find_edge_shared_vertex(e1id, e2id))
    return 0;
  var res = is_joint_smooth(e1id, e2id);
  return (res > 0);
}

export function uil_is_joint_geom_smooth(e1id, e2id) {
  var res = is_joint_geom_smooth(e1id, e2id);
  return (res > 0);
}

export function uil_is_vertex_of_face(vid, fid) {
  var res = is_vertex_of_face(vid, fid);
  return res;
}

export function uil_is_edge_of_face(eid, fid) {
  var edge_ids = uil_get_edges_of_face(fid);
  if (edge_ids.indexOf(eid) == -1 )
    return false;
  else
    return true;
}

export function uil_is_edge_of_vertex(eid, vid) {
  var edge_ids = uil_get_edges_of_vertex(vid);
  if (edge_ids.indexOf(eid) == -1 )
    return false;
  else
    return true;
}


export function uil_is_object_fixed(id) {
  if (isEdge(id) || isVertex(id))
    return is_object_fixed(id);
  else if (isFace(id)) {
    // Not actually storing fixing information for faces. A face is considered fixed if all edges around it are fixed.
    var is_fixed = true;
    var es = uil_get_edges_of_face(id);
    if (es) {
      es.forEach(function(eid){
        if (!uil_is_object_fixed(eid))
          is_fixed = false;
      });
    }
    else {
      is_fixed = false;
    }
    return is_fixed;
  }
  else
    return 0;
}

export function uil_fix_objects(ids) {
  return change_object_fixedness(ids, true);
}

export function uil_unfix_objects(ids) {
  return change_object_fixedness(ids, false);
}

export function uil_is_object_visible(id) {
  return(is_object_visible(id));
}

export function uil_make_faces_visible(ids) {
  var res = uil_change_object_visibility(ids, true, false);
  ids.forEach(function(id) {
    if (face_is_for_sketch(id))
      current_scene_raynode_for_sketch_objects().add(uid_faces.getItem(id).object3d);
    else
      uid_objects_raynode.add(uid_faces.getItem(id).object3d);
  });

  // Then go through all edges, and make any of them visible if they have at least a visible face on them.
  var edges_to_make_visible = [];
  uid_edges.keys().forEach(function(eid) {
    if (!uil_is_object_visible(eid)) {
      var f_array = uil_get_faces_of_edge(eid);
      if (uil_is_object_visible(f_array[0]) || uil_is_object_visible(f_array[1]))
        push_nodups(eid, edges_to_make_visible);
    }
  });
  var res = uil_change_object_visibility(edges_to_make_visible, true, false);
  if (!App.state.currently_hiding_edges) {
    edges_to_make_visible.forEach(function(eid){
      if (edge_is_for_sketch(eid))
        current_scene_raynode_for_sketch_objects().add(uid_edges.getItem(eid).object3d);
      else
        uid_objects_raynode.add(uid_edges.getItem(eid).object3d);
      uid_visible_edge_mesh_raynode.add(uid_edges.getItem(eid).get_visible_edge_mesh());
    });
  }

  // Then go through all edges again, and make their vertices visible if the edges are visible.
  var vertices_to_make_visible = [];
  uid_edges.keys().forEach(function(eid) {
    if (uil_is_object_visible(eid)) {
      var v_array = uil_get_vertices_of_edge(eid);
      v_array.forEach(function(vid){
        if (!uil_is_object_visible(vid))
          push_nodups(vid, vertices_to_make_visible);
      });
    }
  });
  var res = uil_change_object_visibility(vertices_to_make_visible, true, false);
  if (!App.state.currently_hiding_vertices) {
    vertices_to_make_visible.forEach(function(vid){
      if (vertex_is_for_sketch(vid))
        current_scene_raynode_for_sketch_objects().add(uid_vertices.getItem(vid).object3d);
      else
        uid_objects_raynode.add(uid_vertices.getItem(vid).object3d);
    });
  }
}

export function uil_make_faces_invisible(ids, dont_remove_tangencies = false) {
  //console_log("Making " + ids + " invisible.");

  uid_deselect_all_objects();

  // First make the actual face invisible by removing it from the objects raynode
  ids.forEach(function(id){
    uid_objects_raynode.remove(uid_faces.getItem(id).object3d);
  });

  var res = uil_change_object_visibility(ids, false, false);

  // Then go through all edges and see if any have invisible faces on either side, in which case make
  // the edge invisible too.
  var edges_to_make_invisible = [];
  uid_edges.keys().forEach(function(eid) {
    if (uil_is_object_visible(eid)) {
      var f_array = uil_get_faces_of_edge(eid);
      if (!uil_is_object_visible(f_array[0]) && !uil_is_object_visible(f_array[1])) {
        // Check if the edge is tangent with any other edge, and if so remove tangency.
        if (!dont_remove_tangencies) {
            var adjacent_edges = [];
            var edge_vertices = uil_get_vertices_of_edge(eid);
            var e_array1 = uil_get_edges_of_vertex(edge_vertices[0]);
            var e_array2 = uil_get_edges_of_vertex(edge_vertices[1]);
            adjacent_edges = e_array1.concat(e_array2);
            adjacent_edges.forEach(function(aeid){
                if (uil_find_edge_shared_vertex(eid, aeid) != 0 && uil_is_joint_smooth(eid, aeid))
                    uil_remove_joint_smoothness([[eid, aeid]], false, true);
            });
        }
        push_nodups(eid, edges_to_make_invisible);
      }
    }
  });
  var res = uil_change_object_visibility(edges_to_make_invisible, false, false);
  edges_to_make_invisible.forEach(function(eid){
    uid_objects_raynode.remove(uid_edges.getItem(eid).object3d);
    uid_visible_edge_mesh_raynode.remove(uid_edges.getItem(eid).get_visible_edge_mesh());
  });

  // Then go through all vertices and see if it has only invisible edges only on it, in which case
  // make the vertex invisible too.
  var vertices_to_make_invisible = [];
  uid_vertices.keys().forEach(function(vid) {
    if (uil_is_object_visible(vid)) {
      var e_array = uil_get_edges_of_vertex(vid);
      var all_invisible = true;
      e_array.forEach(function(eid){
        if (uil_is_object_visible(eid))
          all_invisible = false;
      });
      if (all_invisible)
        push_nodups(vid, vertices_to_make_invisible);
    }
  });
  var res = uil_change_object_visibility(vertices_to_make_invisible, false, false);
  vertices_to_make_invisible.forEach(function(vid){
    uid_objects_raynode.remove(uid_vertices.getItem(vid).object3d);
  });

  return res;
}

export function uil_copy_bodies(body_ids, dont_attach_manipulator_afterwards = false) {
  var all_ok = true;
  var all_bad = true;
  var copied_body_ids = [];
  suppress_undo_tracking();
  suppress_animate();

  body_ids.forEach(function(bid){
    var res = copy_body(bid);
    if (res == 0)
      all_ok = false;
    else {
      copied_body_ids.push(res);
      all_bad = false;
    }
  });

  if (all_bad) {
    display_message_to_user(LOG.WARN, "Unable to copy bodies.");
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    request_setting_button_states();
    return [];
  }

  if (!all_ok)
    display_message_to_user(LOG.WARN, "Some bodies failed to copy.");

  // Make sure copied body is unfixed
  var objects_to_unfix = [];
  uid_faces.keys().forEach(function(fid){
    if (copied_body_ids.indexOf(uil_get_body_of_face(fid)) != -1) {
      if (uil_is_object_fixed(fid)) {
        push_nodups(fid, objects_to_unfix);
      }
      var es = uil_get_edges_of_face(fid);
      es.forEach(function(eid){
        if (uil_is_object_fixed(eid)) {
          push_nodups(eid, objects_to_unfix);
        }
        var vs = uil_get_vertices_of_edge(eid);
        vs.forEach(function(vid) {
          if (uil_is_object_fixed(vid)) {
            push_nodups(vid, objects_to_unfix);
          }
        });
      });
    }
  });

  if (objects_to_unfix.length > 0) {
    uid_set_fixing_of_objects(objects_to_unfix, false);
  }

  uid_deselect_all_objects();

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  request_setting_button_states();

  if (!dont_attach_manipulator_afterwards) {
    uid_faces.keys().forEach(function(fid){
      if (uil_is_object_visible(fid) && copied_body_ids.indexOf(uil_get_body_of_face(fid)) != -1)
        uid_select_object(fid);
    });
    uid_state.rigid_move_manipulator_shown_after_copy_body = true;
    uid_show_rigid_move_manipulator(false, 0, false, false, false, false, false, true);
  }
  return copied_body_ids;
}

export function uil_save_model(compression_level = 0, extra_data_fac = 1) {
  // Fill in extra_data
  var extra_data_string = create_extra_data_string();

  uil_set_save_model_compression_level(compression_level);
  var s = save_model(extra_data_string, extra_data_fac);
  return s;
}

export function uil_get_world_scaling_factor() {
  return get_world_scaling_factor();
}

export function uil_set_world_scaling_factor(factor) {
  return set_world_scaling_factor(factor);
}

export function uil_set_add_edge_pair_tangencies_on_load(on) {
  return set_add_edge_pair_tangencies_on_load(on);
}

export function uil_handle_pre_core_action(arguments_array) {
  if (App.state.tangencies_are_displayed || App.state.editing_edge_tangent_mode) {
    if (arguments_array && arguments_array.indexOf("save_model") != -1) {
      // We're saving the model, don't hide the tangencies as they'll disappear with each autosave.
    }
    else if (uid_raynode == uid_manip_raynode && uid_rigid_move_manipulator.is_for_edge_tangent) {
      // We're rotating the tangent, so don't hide it.
    }
    else {
      hide_tangencies(true);
    }
  }

  if (arguments_array && arguments_array.indexOf("dragging") != -1) {
    // If we are in dragging mode, we don't need to do anything.
  }
  else {

    if (!(arguments_array && arguments_array.indexOf("dont_affect_modified_objects") != -1) && !in_compound_operation()) {
      user_modified_objects_hash.clear();
      core_modified_objects_hash.clear();
    }

    if (arguments_array && arguments_array.indexOf("no coords") != -1)
      hide_indicated_vertices();

    if (arguments_array && arguments_array.indexOf("clear face materials") != -1) {
      uid_callback_face_material = null;
      uid_callback_face_color = null;
      uid_last_used_face_material_name = "plastic";
    }
    if (arguments_array && arguments_array.indexOf("phi_add_vertex") != -1) {
      uid_current_api_call = "phi_add_vertex";
      uid_api_add_vertex_new_edge1_id = 0;
      uid_api_add_vertex_new_edge2_id = 0;
    }
    if (arguments_array && arguments_array.indexOf("phi_add_edge") != -1) {
      uid_current_api_call = "phi_add_edge";
      uid_api_add_edge_new_face1_id = 0;
      uid_api_add_edge_new_face2_id = 0;
    }
    if (arguments_array && arguments_array.indexOf("phi_pop") != -1) {
      uid_current_api_call = "phi_pop";
      uid_api_pop_faces = [];
    }
  }

  if (arguments_array && arguments_array.indexOf("phi_undo") != -1) {
    uid_current_api_call = "phi_undo";
  }
  if (arguments_array && arguments_array.indexOf("phi_redo") != -1) {
    uid_current_api_call = "phi_redo";
  }
  suppress_animate();
}

export function uil_handle_post_core_action(arguments_array) {
  if (!arguments_array || !(arguments_array.includes("save_model"))) {
    App.state.activity_timestamp = Date.now();
  }

  if (App.state.shadows_visible) {
    update_shadow_light_dimensions(true);
    renderer.shadowMap.needsUpdate = true;
  }

  if (arguments_array && arguments_array.indexOf("dragging") != -1) {
    // If we are in dragging mode, only check if we need to make edges symmetric
    // Except if we just started a drag, in which case we don't.
    if (App.state.symmetry_plane_selected && arguments_array.indexOf("start_drag") == -1)
      uid_make_edges_symmetric(false, false);

    // Incrememnt drag step count
    uid_state.drag_step_count++;
  }
  else {
    // Reset drag step count
    uid_state.drag_step_count = 0;

    // If we're at an end drag, hide any moving coordinates that were displayed.
    if (moving_coordinates_visible.value && arguments_array && arguments_array.indexOf("end_drag") != -1) {
      hide_indicated_vertices();
    }

    if (arguments_array && arguments_array.indexOf("phi_add_vertex") != -1) {
      uid_current_api_call = "";
    }

    if (arguments_array && arguments_array.indexOf("phi_add_edge") != -1) {
      uid_current_api_call = "";
    }

    if (arguments_array && arguments_array.indexOf("phi_pop") != -1) {
      uid_current_api_call = "";
    }

    force_refresh();

    // Re-apply any symmetries if necessary
    if (App.state.symmetry_plane_selected &&
      (!arguments_array ||
        (arguments_array.indexOf("phi_undo") == -1 &&
        arguments_array.indexOf("phi_redo") == -1 &&
        arguments_array.indexOf("phi_make_edges_symmetric") == -1 &&
        arguments_array.indexOf("dont_propagate_to_symmetry") == -1 &&
        arguments_array.indexOf("end_drag") == -1))) {
          suppress_undo_tracking();
          uid_make_edges_symmetric(false, false);
          unsuppress_undo_tracking_if_ready(true);
    }

    // re_propagate_fixing_to_all_objects();

    if (App.state.shadows_visible)
      update_shadow_light_dimensions(false);
      if (App.state.selected_coordinates_visible)
        indicate_selected_vertices();

    if (!arguments_array || (arguments_array.indexOf("save_model") == -1 && arguments_array.indexOf("load_model") == -1)) {
      // Unless we're saving the model or loading a new one, set the dirty flag.
      uid_set_dirty_flag();
      update_dom_text_values_and_coordinates();
    }

    // Not displaying tangency texts for now.
    // recreate_tangency_texts();

    if (arguments_array && arguments_array.indexOf("phi_undo") == -1 && arguments_array.indexOf("phi_redo") == -1) {
      if (!App.state.symmetry_plane_selected &&
        (App.state.always_run_consistency_checks_on_vertices || App.state.always_run_consistency_checks_on_rest)) {
        // Check model integrity and display issues
        var is_start_drag = (arguments_array && arguments_array.indexOf("start_drag") != -1);
        var is_end_drag = (arguments_array && arguments_array.indexOf("end_drag") != -1);
        suppress_undo_tracking();
        var issues_found = check_model_integrity(false, is_start_drag, is_end_drag, true, App.state.always_run_consistency_checks_on_vertices, App.state.always_run_consistency_checks_on_rest);
        unsuppress_undo_tracking_if_ready(true);
      }
    }

    if (App.state.dragging_type == "none")
      adjust_camera_position_along_axis_and_depth(uid_camera);
  }

  if (must_redraw_sketches) {
    suppress_undo_tracking();
    redraw_sketches();
    unsuppress_undo_tracking_if_ready(true);
    must_redraw_sketches = false;
  }

  if (arguments_array && arguments_array.indexOf("phi_undo") != -1)
    uid_current_api_call = "";
  if (arguments_array && arguments_array.indexOf("phi_redo") != -1)
    uid_current_api_call = "";

  // XXX
  // project_stored_lines_onto_surfaces();
  // XXX

  // If curvatures are visible, recreate them
  if (curvature_edges.value.length > 0) {
    show_edge_curvatures();
  }

  if (arguments_array && (arguments_array.indexOf("dragging") != -1 || arguments_array.indexOf("dont_affect_modified_objects") != -1)) {
    // don't
  }
  /*
  else if (arguments_array && arguments_array.indexOf("phi_set_display_face_triangulation_res") != -1) {
    // don't
  }
  else if (arguments_array && arguments_array.indexOf("phi_get_display_face_triangulation_res") != -1){
    // don't
  }
  */
  else if (arguments_array && arguments_array.indexOf("phi_change_object_visibility") != -1){
    // don't
  }
  else if (in_ui_side_compound_operation() || in_compound_operation()) {
    // don't
  }
  else {
    setup_checking_for_zero_thickness_faces(arguments_array);
  }

  // Re-display tangencies
  if (App.state.tangencies_are_displayed || App.state.editing_edge_tangent_mode ||
    uid_raynode == uid_manip_raynode && uid_rigid_move_manipulator.is_for_edge_tangent) {
      hide_tangencies(true);
      display_tangencies(false, true);
  }

  // If we are at a drag end, we need to re-apply symmetries and then clear the symmetric edges hash
  if (App.state.symmetry_plane_selected && arguments_array && arguments_array.indexOf("end_drag") != -1 && !in_compound_operation()) {
    suppress_undo_tracking();
    uid_make_edges_symmetric(false, false);
    unsuppress_undo_tracking_if_ready(false);
    user_modified_objects_hash.clear();
    core_modified_objects_hash.clear();
    core_entire_drag_modified_objects_hash.clear();
  }

  /* Carefully decide WHEN to do this!
  if (!arguments_array || arguments_array.indexOf("dragging") == -1) {
    properly_split_invisible_face_connected_components();
  }
  */


  unsuppress_animate_if_ready();
}

export function setup_checking_for_zero_thickness_faces(arguments_array) {

  // Don't do this for large models.
  if (uid_faces.length > 30)
    return false;

  zero_thickness_edge_list_to_check = [];
  if (arguments_array && (arguments_array.indexOf("dragging") != -1 ||
      arguments_array.indexOf("dont_affect_modified_objects") != -1))
    return;

  if (arguments_array && arguments_array.indexOf("phi_set_display_face_triangulation_res") != -1)
    return;
  else if (arguments_array && arguments_array.indexOf("phi_get_display_face_triangulation_res") != -1)
    return;

  if (in_ui_side_compound_operation() || in_compound_operation())
    return;

  // Check that zero thickness hash has valid objects
  var faces_to_remove = [];
  zero_thickness_faces_hash.keys().forEach(function(fid) {
    if (!isFace(fid))
      faces_to_remove.push(fid);
  });
  faces_to_remove.forEach(function(fid){
    zero_thickness_faces_hash.removeItem(fid);
  });

  if (uid_edges.length < 100) {
    // If we have less than 100 edges don't go into the trouble of working on a specific set of objects, consider all.
    uid_edges.keys().forEach(function(eid){
      zero_thickness_edge_list_to_check.push(eid);
    });
  }
  else if (arguments_array) {
    if (arguments_array.indexOf("load_model") != -1 || arguments_array.indexOf("delete_objects") != -1 ||
        arguments_array.indexOf("phi_redo") != -1 || arguments_array.indexOf("phi_undo") != -1) {
      // Check all objects
      uid_edges.keys().forEach(function(eid){
        zero_thickness_edge_list_to_check.push(eid);
      });
    }
    else {
      // Check only modified objects and those already in the 0 thickness hash
      user_modified_objects_hash.values().forEach(function(o) {
        if (o && o.type == "edge") {
          if (zero_thickness_edge_list_to_check.indexOf(o.id) == -1)
          zero_thickness_edge_list_to_check.push(o.id);
        }
      });

      zero_thickness_faces_hash.keys().forEach(function(fid){
        var es = uil_get_edges_of_face(fid);
        es.forEach(function(eid){
          if (zero_thickness_edge_list_to_check.indexOf(eid) == -1)
          zero_thickness_edge_list_to_check.push(eid);
        });
      });
    }
  }
  else {
    uid_edges.keys().forEach(function(eid){
      zero_thickness_edge_list_to_check.push(eid);
    });
  }
}

export function check_for_zero_thickness_faces() {

  // Don't do this for large models.
  if (uid_faces.length > 30)
    return;

  // Check whether we should run this or not, and on which objects
  if (in_ui_side_compound_operation() || in_compound_operation() || animation_is_suppressed() || suppress_zero_thickness_checking())
    return;

  if (zero_thickness_edge_list_to_check.length == 0)
    return;

  // Check that zero thickness hash has valid objects
  var faces_to_remove = [];
  zero_thickness_faces_hash.keys().forEach(function(fid) {
    if (!isFace(fid))
      faces_to_remove.push(fid);
  });
  faces_to_remove.forEach(function(fid){
    zero_thickness_faces_hash.removeItem(fid);
  });

  // Use two arrays to mark faces that may have lost their 0-thickness, and faces that still
  // have it, in order to be able to remove the former from the hash.
  var faces_currently_zero_thickness = [];
  var faces_to_remove_zero_thickness = [];
  zero_thickness_edge_list_to_check.forEach(function(eid) {
    var fs = uil_get_faces_of_edge(eid);
    var f1 = fs[0];
    var f2 = fs[1];
    if (uil_is_object_visible(f1) && uil_is_object_visible(f2)) {
      if (check_if_faces_are_0_thickness(f1,f2)) {
        var b = new THREE.Vector3();
        var d = new THREE.Vector3();
        var n = new THREE.Vector3();
        var wv_r = new WrappedValue(0);
        var wv_f = new WrappedValue(0);
        uil_get_face_geometry(f1, b, n, wv_r, d, wv_f);
        n.multiplyScalar(wv_r.v/20000.0);
        zero_thickness_faces_hash.setItem(f1, n);

        var b = new THREE.Vector3();
        var d = new THREE.Vector3();
        var n = new THREE.Vector3();
        var wv_r = new WrappedValue(0);
        var wv_f = new WrappedValue(0);
        uil_get_face_geometry(f2, b, n, wv_r, d, wv_f);
        n.multiplyScalar(wv_r.v/20000.0);
        zero_thickness_faces_hash.setItem(f2,n);

        if (faces_currently_zero_thickness.indexOf(f1) == -1)
          faces_currently_zero_thickness.push(f1)
        if (faces_currently_zero_thickness.indexOf(f2) == -1)
          faces_currently_zero_thickness.push(f2)
      }
      else {
        if (faces_to_remove_zero_thickness.indexOf(f1) == -1)
          faces_to_remove_zero_thickness.push(f1)
        if (faces_to_remove_zero_thickness.indexOf(f2) == -1)
          faces_to_remove_zero_thickness.push(f2)
      }
    }
  });

  // Remove faces that have lost 0 thickness
  var removed_faces_from_hash = false;
  faces_to_remove_zero_thickness.forEach(function(fid) {
    if (faces_currently_zero_thickness.indexOf(fid) == -1) {
      zero_thickness_faces_hash.removeItem(fid);
      removed_faces_from_hash = true;
    }
  });

  zero_thickness_faces_hash.keys().forEach(function(fid) {
    redisplay_face(fid);
  });
  faces_to_remove_zero_thickness.forEach(function(fid) {
  if (fid && !zero_thickness_faces_hash.hasItem(fid))
    redisplay_face(fid);
  });

  zero_thickness_edge_list_to_check = [];
  animate();
}

export function check_for_zero_thickness_faces_after_core(arguments_array) {
  // Check whether we should run this or not, and on which objects
  if (in_ui_side_compound_operation() || in_compound_operation() || App.state.dragging_type != "none" || uid_in_dragging_mode)
    return;

  // Check that zero thickness has has valid objects
  var faces_to_remove = [];
  zero_thickness_faces_hash.keys().forEach(function(fid) {
    if (!isFace(fid))
      faces_to_remove.push(fid);
  });
  faces_to_remove.forEach(function(fid){
    zero_thickness_faces_hash.removeItem(fid);
  });

  var edge_list = [];

  if (uid_edges.length < 100) {
    // If we have less than 100 edges don't go into the trouble of working on a specific set of objects, consider all.
    uid_edges.keys().forEach(function(eid){
      edge_list.push(eid);
    });
  }
  else if (arguments_array) {
    if (arguments_array.indexOf("load_model") != -1 || arguments_array.indexOf("delete_objects") != -1 ||
        arguments_array.indexOf("phi_redo") != -1 || arguments_array.indexOf("phi_undo") != -1) {
      // Check all objects
      uid_edges.keys().forEach(function(eid){
        edge_list.push(eid);
      });
    }
    else {
      // Check only modified objects and those already in the 0 thickness hash
      user_modified_objects_hash.values().forEach(function(o) {
        if (o.type == "edge") {
          if (edge_list.indexOf(o.id) == -1)
            edge_list.push(o.id);
        }
      });

      zero_thickness_faces_hash.keys().forEach(function(fid){
        var es = uil_get_edges_of_face(fid);
        es.forEach(function(eid){
          if (edge_list.indexOf(eid) == -1)
            edge_list.push(eid);
        });
      });
    }
  }
  else {
    uid_edges.keys().forEach(function(eid){
      edge_list.push(eid);
    });
  }

  // Use two arrays to mark faces that may have lost their 0-thickness, and faces that still
  // have it, in order to be able to remove the former from the hash.
  var faces_currently_zero_thickness = [];
  var faces_to_remove_zero_thickness = [];
  edge_list.forEach(function(eid) {
    var fs = uil_get_faces_of_edge(eid);
    var f1 = fs[0];
    var f2 = fs[1];
    if (uil_is_object_visible(f1) && uil_is_object_visible(f2)) {
      if (check_if_faces_are_0_thickness(f1,f2)) {
        var b = new THREE.Vector3();
        var d = new THREE.Vector3();
        var n = new THREE.Vector3();
        var wv_r = new WrappedValue(0);
        var wv_f = new WrappedValue(0);
        uil_get_face_geometry(f1, b, n, wv_r, d, wv_f);
        n.multiplyScalar(wv_r.v/2000.0);
        zero_thickness_faces_hash.setItem(f1, n);

        var b = new THREE.Vector3();
        var d = new THREE.Vector3();
        var n = new THREE.Vector3();
        var wv_r = new WrappedValue(0);
        var wv_f = new WrappedValue(0);
        uil_get_face_geometry(f2, b, n, wv_r, d, wv_f);
        n.multiplyScalar(wv_r.v/2000.0);
        zero_thickness_faces_hash.setItem(f2,n);

        if (faces_currently_zero_thickness.indexOf(f1) == -1)
          faces_currently_zero_thickness.push(f1)
        if (faces_currently_zero_thickness.indexOf(f2) == -1)
          faces_currently_zero_thickness.push(f2)
      }
      else {
        if (faces_to_remove_zero_thickness.indexOf(f1) == -1)
          faces_to_remove_zero_thickness.push(f1)
        if (faces_to_remove_zero_thickness.indexOf(f2) == -1)
          faces_to_remove_zero_thickness.push(f2)
      }
    }
  });

  // Remove faces that have lost 0 thickness
  var removed_faces_from_hash = false;
  faces_to_remove_zero_thickness.forEach(function(fid) {
    if (faces_currently_zero_thickness.indexOf(fid) == -1) {
      zero_thickness_faces_hash.removeItem(fid);
      removed_faces_from_hash = true;
    }
  });

  // If we just loaded a new model, or we just hit undo or redo, or there is at least one face marked as 0-thickness recreate all faces
  // But be careful not to do this after a call to phi_set_display_face_triangulation_res, since this is what refresh_all_faces() uses
  // and it will get us in an infinite loop.
  var must_refresh_faces = false;
  if (arguments_array && arguments_array.indexOf("phi_set_display_face_triangulation_res") != -1)
    must_refresh_faces = false;
  else if (arguments_array && arguments_array.indexOf("phi_get_display_face_triangulation_res") != -1)
    must_refresh_faces = false;
  else if (arguments_array && arguments_array.indexOf("load_model") != -1)
    must_refresh_faces = true;
  else if (arguments_array && arguments_array.indexOf("phi_redo") != -1)
    must_refresh_faces = true;
  else if (arguments_array && arguments_array.indexOf("phi_undo") != -1)
    must_refresh_faces = true;
  else if (zero_thickness_faces_hash && zero_thickness_faces_hash.length > 0)
    must_refresh_faces = true;
  else if (removed_faces_from_hash)
    must_refresh_faces = true;

  if (must_refresh_faces) {
    /*
    uid_faces.keys().forEach(function(fid) {
      redisplay_face(fid);
    });
    */
   zero_thickness_faces_hash.keys().forEach(function(fid) {
    redisplay_face(fid);
   });
   faces_to_remove_zero_thickness.forEach(function(fid) {
    if (fid && !zero_thickness_faces_hash.hasItem(fid))
      redisplay_face(fid);
   });
  }
  animate();
}


export function uil_calc_ray_dir_for_face_sampling(f_id, v0_id, v1_id, linedir) {
  // linedir is passed to receive the resulting vector.
  return calc_ray_dir_for_face_sampling(f_id, v0_id, v1_id, linedir);
}

export function uil_stop_undo_tracking() {
  return stop_undo_tracking();
}

export function uil_start_undo_tracking() {
  if (start_undo_tracking()) {
    force_refresh();
    return 1;
  } else
    return 0;
}

export function uil_save_undo_state() {
  // Make sure core face tessellation is not suppressed
  if (core_face_tessellation_is_suppressed())
    reset_core_face_tessellation();

  save_undo_state();
  force_refresh();
}

export function uil_start_compound_operation() {
  suppress_animate();
  uid_in_compound_operation = true;
  return start_compound_operation();
}

export function in_compound_operation() {
  return uid_in_compound_operation;
}

export function uil_end_compound_operation() {
  uid_in_compound_operation = false;
  var res = end_compound_operation();

  // Re-display tangencies if needed
  if (App.state.tangencies_are_displayed || App.state.editing_edge_tangent_mode) {
      hide_tangencies(true);
      display_tangencies(false, true);
  }

  // Re-apply any symmetries if necessary
  if (App.state.symmetry_plane_selected)
    uid_make_edges_symmetric(false, false);

  user_modified_objects_hash.clear();
  core_modified_objects_hash.clear();

  unsuppress_animate_if_ready();
  return res;
}

export function uil_body_objects(bid) {
    const faces = new Set(faces_of_body(bid));
    const edges = new Set(bind(uil_get_edges_of_face)(faces));
    const vertices = new Set(bind(uil_get_vertices_of_edge)(edges));
    return { faces, edges, vertices };
}

export function uil_select_body(bid) {
    pipe(
        [
            uid_vertices.keys().filter(x => uil_get_body_of_vertex(x) === bid),
            uid_edges.keys().filter(x => uil_get_body_of_edge(x) === bid),
            uid_faces.keys().filter(x => uil_get_body_of_face(x) === bid),
        ],
        flatten,
        each(uid_select_object),
    );
}
